import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  useTheme as useMuiTheme,
  useMediaQuery,
  CssBaseline,
  Collapse,
  Badge,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { BASE_URL, BIM_SYSTEM_URL, FILES_SYSTEM_URL, TASK_PROJECT_DASHBOARD_URL } from '../../config';
import axiosInstance from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';
import CreateIncomingDocumentDialog from '../../pages/IncomingDocuments/components/CreateIncomingDocumentDialog';
import CreateDocumentDialog from '../../pages/OutgoingDocuments/components/CreateDocumentDialog';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  ExpandLess,
  ExpandMore,
  Person as PersonIcon,
  Settings as SettingsIcon,
  ViewInAr as BimIcon,
  Folder as FolderIcon,
  Dashboard as DashboardIcon,
  AccountTree as AccountTreeIcon,
} from '@mui/icons-material';
import TelegramIcon from '@mui/icons-material/Telegram';
import { AlertColor } from '@mui/material/Alert';
import NotificationBell from './NotificationBell';

const expandedWidth = 265;
const collapsedWidth = 51;

// Interface cho menu item
interface MenuItem {
  text?: string;
  path: string;
  icon?: React.ReactNode;
  divider?: boolean;
  submenu?: MenuItem[];
  badge?: number;
  isExternal?: boolean;
}

// Interface cho role
interface Role {
  code: string;
  name: string;
}

// Interface cho user
interface User {
  id: string;
  email: string;
  fullname: string;
  username: string;
  department: string;
  position: string;
  roles: Role[];
  current_role: Role;
  is_active: boolean;
}

// Interface cho badge counts từ API statistics
interface BadgeCounts {
  // Quản lý văn bản đến (chỉ cho TRUONG_PHONG_HCTC, VANTHU_DEN, VANTHU_DI)
  qlvb_den_cho_xu_ly?: number;
  qlvb_den_du_thao?: number;
  qlvb_den_dien_tu?: number;
  
  // Xử lý văn bản đến
  xulyvb_den_cho_xu_ly: number;
  xulyvb_den_tra_lai: number;
  xulyvb_den_xem_de_biet_chua_xem?: number;
  
  // Xử lý văn bản đi
  xulyvb_di_du_thao: number;
  xulyvb_di_can_xu_ly: number;
  
  // Văn bản nội bộ
  xulyvb_noibo_can_xu_ly: number;
  xulyvb_noibo_du_thao: number;
  xulyvb_noibo_xem_de_biet_chua_xem?: number;
  
  // Xử lý văn bản đi xem để biết chưa xem
  xulyvb_di_xem_de_biet_chua_xem?: number;
  
  // Công việc
  congviec_kehoach_thang_chua_thuc_hien?: number;
  congviec_kehoach_thang_dang_thuc_hien?: number;
  congviec_kehoach_thang_da_hoanthanh?: number;
  congviec_cddt_chua_thuc_hien?: number;
  congviec_cddt_dang_thuc_hien?: number;
  congviec_cddt_da_hoanthanh?: number;
}

// Menu items cho role NHAN_VIEN
const nhanVienMenuItems: MenuItem[] = [
  { text: 'Dashboard', path: '/dashboard', icon: <HomeIcon /> },
  { 
    text: 'Xử lý hồ sơ công việc', 
    path: '#', 
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M 12 9.359375 C 11.117188 9.359375 10.398438 10.078125 10.398438 10.960938 L 10.398438 17.761719 C 10.398438 18.644531 11.117188 19.359375 12 19.359375 C 12.882812 19.359375 13.601562 18.644531 13.601562 17.761719 L 13.601562 10.960938 C 13.601562 10.078125 12.882812 9.359375 12 9.359375 Z M 12 9.359375 "/>
    <path d="M 12 0 C 5.390625 0 0 5.390625 0 12 C 0 18.609375 5.390625 24 12 24 C 18.609375 24 24 18.609375 24 12 C 24 5.390625 18.609375 0 12 0 Z M 12 3.199219 C 16.878906 3.199219 20.800781 7.121094 20.800781 12 C 20.800781 16.878906 16.878906 20.800781 12 20.800781 C 7.121094 20.800781 3.199219 16.878906 3.199219 12 C 3.199219 7.121094 7.121094 3.199219 12 3.199219 Z M 12 3.199219 "/>
    <path d="M 12 4.398438 C 10.914062 4.398438 10 5.3125 10 6.398438 C 10 7.484375 10.914062 8.398438 12 8.398438 C 13.085938 8.398438 14 7.484375 14 6.398438 C 14 5.3125 13.085938 4.398438 12 4.398438 Z M 12 4.398438 "/>
    </svg>,
    submenu: [
      { 
        text: 'Xử lý văn bản đến', 
        path: '#', 
        badge: 12,
        submenu: [
          { text: 'Văn bản đến chờ xử lý', path: '/xlvb/incoming-documents/?submenu=van_ban_cho_xu_ly', badge: 8 },
          { text: 'Văn bản đến trả lại', path: '/xlvb/incoming-documents/?submenu=van_ban_den_tra_lai', badge: 3 },
          { text: 'Văn bản đến đã xử lý', path: '/xlvb/incoming-documents/?submenu=van_ban_da_xu_ly', badge: 3 },
          { text: 'Văn bản xem để biết', path: '/xlvb/incoming-documents/?submenu=van_ban_xem_de_biet', badge: 1 },
        ]
      },
      { 
        text: 'Xử lý văn bản đi', 
        path: '#', 
        badge: 7,
        submenu: [
          { text: 'Văn bản đi dự thảo', path: '/xlvb/outgoing-documents/?submenu=van_ban_di_du_thao', badge: 2 },
          { text: 'Văn bản đi cần xử lý', path: '/xlvb/outgoing-documents/?submenu=van_ban_di_can_xu_ly', badge: 3 },
          { text: 'Văn bản đi đã xử lý', path: '/xlvb/outgoing-documents/?submenu=van_ban_di_da_xu_ly', badge: 1 },
          { text: 'Văn bản đi đã ban hành', path: '/xlvb/outgoing-documents/?submenu=van_ban_di_da_ban_hanh', badge: 1 },
          { text: 'Văn bản đi xem để biết', path: '/xlvb/outgoing-documents/?submenu=van_ban_di_xem_de_biet', badge: 1 },

        ]
      },
      { 
        text: 'Văn bản đảng/nội bộ', 
        path: '#', 
        submenu: [
          { text: 'Văn bản dự thảo', path: '/xlvb/internal-documents/?submenu=van_ban_duthao', badge: 2 },
          { text: 'Văn bản cần xử lý', path: '/xlvb/internal-documents/?submenu=van_ban_di_can_xu_ly', badge: 3 },
          { text: 'Văn bản đã xử lý', path: '/xlvb/internal-documents/?submenu=van_ban_di_da_xu_ly', badge: 1 },
          { text: 'Văn bản đã ban hành', path: '/xlvb/internal-documents/?submenu=van_ban_di_da_ban_hanh', badge: 1 },
          { text: 'Văn bản xem để biết', path: '/xlvb/internal-documents/?submenu=van_ban_di_xem_de_biet', badge: 1 },
        ]
      },

    ]
  },
  { 
    text: 'Tra cứu văn bản', 
    path: '/tra-cuu-toanbo', 
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d="M9.29289 1.29289C9.48043 1.10536 9.73478 1 10 1H18C19.6569 1 21 2.34315 21 4V8C21 8.55228 20.5523 9 20 9C19.4477 9 19 8.55228 19 8V4C19 3.44772 18.5523 3 18 3H11V8C11 8.55228 10.5523 9 10 9H5V20C5 20.5523 5.44772 21 6 21H10C10.5523 21 11 21.4477 11 22C11 22.5523 10.5523 23 10 23H6C4.34315 23 3 21.6569 3 20V8C3 7.73478 3.10536 7.48043 3.29289 7.29289L9.29289 1.29289ZM6.41421 7H9V4.41421L6.41421 7ZM20.1716 18.7574C20.6951 17.967 21 17.0191 21 16C21 13.2386 18.7614 11 16 11C13.2386 11 11 13.2386 11 16C11 18.7614 13.2386 21 16 21C17.0191 21 17.967 20.6951 18.7574 20.1716L21.2929 22.7071C21.6834 23.0976 22.3166 23.0976 22.7071 22.7071C23.0976 22.3166 23.0976 21.6834 22.7071 21.2929L20.1716 18.7574ZM13 16C13 14.3431 14.3431 13 16 13C17.6569 13 19 14.3431 19 16C19 17.6569 17.6569 19 16 19C14.3431 19 13 17.6569 13 16Z" fill="currentColor"/>
    </svg>,
    submenu: [
      { text: 'Tra cứu toàn bộ VB', path: '/tra-cuu-toanbo' },
      { text: 'Tra cứu văn bản đến', path: '/tra-cuu-vbden' },
      { text: 'Tra cứu văn bản đi', path: '/tra-cuu-vbdi' },
      { text: 'Tra cứu VB đến liên quan', path: '/tra-cuu-vbden-lienquan' },
    ]
  },
  { text: 'Quản lý công việc nội bộ', path: '#', icon: <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
    <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M 11 4 L 7.199219 4 C 6.078125 4 5.519531 4 5.09375 4.21875 C 4.714844 4.410156 4.410156 4.714844 4.21875 5.09375 C 4 5.519531 4 6.078125 4 7.199219 L 4 16.800781 C 4 17.921875 4 18.480469 4.21875 18.90625 C 4.410156 19.285156 4.714844 19.589844 5.09375 19.78125 C 5.519531 20 6.078125 20 7.199219 20 L 16.800781 20 C 17.921875 20 18.480469 20 18.90625 19.78125 C 19.285156 19.589844 19.589844 19.285156 19.78125 18.90625 C 20 18.480469 20 17.921875 20 16.800781 L 20 12.5 M 15.5 5.5 L 18.328125 8.328125 M 10.761719 10.238281 L 17.410156 3.589844 C 18.191406 2.808594 19.457031 2.808594 20.238281 3.589844 C 21.019531 4.371094 21.019531 5.636719 20.238281 6.417969 L 13.378906 13.28125 C 12.617188 14.042969 12.234375 14.421875 11.800781 14.726562 C 11.417969 14.992188 11 15.214844 10.5625 15.386719 C 10.070312 15.582031 9.542969 15.6875 8.488281 15.902344 L 8 16 L 8.046875 15.667969 C 8.214844 14.492188 8.300781 13.90625 8.492188 13.355469 C 8.660156 12.871094 8.890625 12.40625 9.179688 11.980469 C 9.503906 11.496094 9.921875 11.078125 10.761719 10.238281 Z M 10.761719 10.238281 "/>
    </svg>,
    badge: 3,
    submenu: [
      { text: 'Công việc', path: '#',
        submenu: [
          { text: 'Tất cả công việc nội bộ', path: '/tasks-new' },
          { text: 'Kế hoạch tháng', path: '/tasks-new/monthly-plan' },
          { text: 'Chỉ đạo điều hành', path: '/tasks-new/directive' },
          { text: 'Công việc từ văn bản', path: '/tasks-new/task-from-docs' },
        ]
      },
      { text: 'Công việc xin gia hạn', path: '#',
        submenu: [
          { text: 'Công việc xin gia hạn chờ duyệt', path: '/tasks-new/request-task-extension' },
          { text: 'Công việc xin gia hạn bị từ chối', path: '/tasks-new/request-task-extension-rejected' },
          { text: 'Công việc xin gia hạn đã duyệt', path: '/tasks-new/request-task-extension-approved' },
        ]
      },
      { text: 'Báo cáo', path: '#',
        submenu: [
          { text: 'Báo cáo tổng hợp cá nhân', path: '/tasks-new/personal-report' },
          { text: 'Báo cáo chi tiết cá nhân', path: '/tasks-new/multiple-personal-detail-reports' },
          { text: 'Báo cáo tổng hợp đơn vị', path: '/tasks-new/department-report' },
        ]
      },
    ]
  },
  { text: 'Lịch công tác', path: '/calendar', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M 4.5 7.5 L 4.5 18.65625 C 4.5 19.265625 5.015625 19.78125 5.648438 19.78125 L 18.585938 19.78125 C 19.21875 19.78125 19.734375 19.265625 19.734375 18.65625 L 19.734375 7.5 C 19.734375 6.890625 19.21875 6.375 18.585938 6.375 L 17.273438 6.375 L 17.273438 6.257812 C 17.273438 5.859375 17.15625 5.414062 16.875 5.085938 C 16.570312 4.78125 16.101562 4.546875 15.421875 4.546875 C 14.71875 4.546875 14.25 4.78125 13.96875 5.085938 C 13.664062 5.414062 13.546875 5.859375 13.546875 6.257812 L 13.546875 6.375 L 10.6875 6.375 L 10.6875 6.257812 C 10.6875 5.859375 10.570312 5.414062 10.289062 5.085938 C 9.984375 4.78125 9.515625 4.546875 8.8125 4.546875 C 8.132812 4.546875 7.664062 4.78125 7.382812 5.085938 C 7.078125 5.414062 6.960938 5.859375 6.960938 6.257812 L 6.960938 6.375 L 5.648438 6.375 C 5.015625 6.375 4.5 6.890625 4.5 7.5 Z M 6.046875 18.257812 L 6.046875 15.820312 L 8.789062 15.820312 L 8.789062 18.257812 Z M 6.046875 15.445312 L 6.046875 13.007812 L 8.789062 13.007812 L 8.789062 15.445312 Z M 6.046875 12.632812 L 6.046875 10.171875 L 8.789062 10.171875 L 8.789062 12.632812 Z M 8.085938 8.226562 L 8.085938 6.257812 C 8.085938 5.882812 8.320312 5.695312 8.8125 5.695312 C 9.328125 5.695312 9.5625 5.882812 9.5625 6.257812 L 9.5625 8.226562 C 9.5625 8.601562 9.328125 8.789062 8.8125 8.789062 C 8.320312 8.789062 8.085938 8.601562 8.085938 8.226562 Z M 9.164062 18.257812 L 9.164062 15.820312 L 11.929688 15.820312 L 11.929688 18.257812 Z M 9.164062 15.445312 L 9.164062 13.007812 L 11.929688 13.007812 L 11.929688 15.445312 Z M 9.164062 12.632812 L 9.164062 10.171875 L 11.929688 10.171875 L 11.929688 12.632812 Z M 12.304688 18.257812 L 12.304688 15.820312 L 15.070312 15.820312 L 15.070312 18.257812 Z M 12.304688 15.445312 L 12.304688 13.007812 L 15.070312 13.007812 L 15.070312 15.445312 Z M 12.304688 12.632812 L 12.304688 10.171875 L 15.070312 10.171875 L 15.070312 12.632812 Z M 15.445312 18.257812 L 15.445312 15.820312 L 18.1875 15.820312 L 18.1875 18.257812 Z M 15.445312 15.445312 L 15.445312 13.007812 L 18.1875 13.007812 L 18.1875 15.445312 Z M 15.445312 12.632812 L 15.445312 10.171875 L 18.1875 10.171875 L 18.1875 12.632812 Z M 14.671875 8.226562 L 14.671875 6.257812 C 14.671875 5.882812 14.90625 5.695312 15.421875 5.695312 C 15.890625 5.695312 16.148438 5.882812 16.148438 6.257812 L 16.148438 8.226562 C 16.148438 8.601562 15.914062 8.789062 15.421875 8.789062 C 14.90625 8.789062 14.671875 8.601562 14.671875 8.226562 Z M 14.671875 8.226562 "/>
    </svg> },
  { text: 'Báo cáo thống kê', path: '#', icon: <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
    <path d="M 3 3 L 21 3 C 22.105469 3 23 3.894531 23 5 L 23 19 C 23 20.105469 22.105469 21 21 21 L 3 21 C 1.894531 21 1 20.105469 1 19 L 1 5 C 1 3.894531 1.894531 3 3 3 Z M 3 5 L 3 19 L 21 19 L 21 5 Z M 9 17 L 7 17 L 7 11 L 9 11 Z M 13 17 L 11 17 L 11 7 L 13 7 Z M 17 17 L 15 17 L 15 10 L 17 10 Z M 17 17 "/>
    </svg>, submenu: [
          { text: 'Báo cáo văn bản đến', path: '/reports/incoming_docs' },
          { text: 'Báo cáo thống kê số liệu trong Cơ quan', path: '/reports/docs-within-organization' }
        ] },
  { text: 'Quản lý tài liệu cá nhân', path: '/hoso-canhan', icon: <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
    <path d="M 23.820312 11.414062 C 23.5625 11.121094 23.113281 11.09375 22.816406 11.351562 L 20.722656 13.207031 L 14.808594 18.441406 C 14.4375 18.769531 13.960938 18.945312 13.472656 18.945312 C 13.332031 18.945312 13.191406 18.933594 13.046875 18.902344 L 9.664062 18.152344 L 5.582031 17.253906 L 2.71875 16.617188 L 2.3125 16.527344 C 2.179688 16.484375 2.066406 16.425781 1.964844 16.351562 C 1.800781 16.234375 1.671875 16.078125 1.585938 15.902344 C 1.496094 15.726562 1.445312 15.53125 1.445312 15.332031 C 1.445312 15.207031 1.46875 15.082031 1.503906 14.957031 C 1.546875 14.820312 1.613281 14.699219 1.691406 14.589844 C 1.804688 14.425781 1.960938 14.296875 2.136719 14.210938 C 2.3125 14.117188 2.507812 14.070312 2.707031 14.070312 C 2.742188 14.070312 2.773438 14.074219 2.808594 14.074219 L 5.757812 14.710938 L 12.601562 16.191406 C 13.449219 16.378906 14.335938 16.148438 14.984375 15.570312 L 19.191406 11.867188 L 23.488281 8.082031 C 23.875 7.738281 24.039062 7.210938 23.90625 6.710938 C 23.777344 6.210938 23.378906 5.828125 22.871094 5.71875 L 22.335938 5.605469 L 18.433594 4.761719 L 13.492188 3.695312 C 12.644531 3.507812 11.761719 3.738281 11.109375 4.3125 L 4.507812 10.113281 L 2.519531 11.859375 L 0.792969 13.375 C 0.722656 13.457031 0.660156 13.539062 0.605469 13.625 C 0.574219 13.664062 0.546875 13.699219 0.523438 13.738281 C 0.351562 13.96875 0.21875 14.234375 0.125 14.523438 C 0.0390625 14.789062 0 15.0625 0 15.332031 C 0 15.910156 0.1875 16.460938 0.515625 16.917969 C 0.679688 17.148438 0.878906 17.351562 1.113281 17.519531 C 1.34375 17.691406 1.609375 17.824219 1.898438 17.914062 L 1.929688 17.921875 L 4.941406 18.582031 L 7.792969 19.207031 L 12.746094 20.292969 C 12.984375 20.34375 13.230469 20.371094 13.472656 20.371094 C 14.304688 20.371094 15.113281 20.070312 15.75 19.511719 L 15.921875 19.363281 L 21.800781 14.15625 L 23.761719 12.421875 C 24.054688 12.160156 24.082031 11.710938 23.820312 11.414062 Z M 23.820312 11.414062 "/>
    </svg> },
  { text: 'Hệ thống Files',
    isExternal: true,
    path: FILES_SYSTEM_URL || '#', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/></svg> },
  { divider: true, path: '' },
];

// Menu riêng cho tra cứu văn bản phòng (VB đến + VB đi theo phòng)
const traCuuVbPhongMenuItem: MenuItem = {
  text: 'Tra cứu văn bản phòng',
  path: '/tra-cuu-vbden?scope=department',
  icon: <AccountTreeIcon />,
  submenu: [
    { text: 'Tra cứu VB đến phòng', path: '/tra-cuu-vbden?scope=department' },
    { text: 'Tra cứu VB đi phòng', path: '/tra-cuu-vbdi?scope=department' },
  ]
};

// Menu items cho role VAN_THU_DI
const vanThuDiMenuItems: MenuItem[] = [
  { text: 'Dashboard', path: '/dashboard', icon: <HomeIcon /> },
  // { 
  //   text: 'Quản lý văn bản đi', 
  //   path: '#', 
  //   icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
  //   <path d="M 23.296875 12.796875 L 15.421875 20.671875 C 14.71875 21.375 13.5 20.882812 13.5 19.875 L 13.5 15.375 L 7.125 15.375 C 6.5 15.375 6 14.875 6 14.25 L 6 9.75 C 6 9.125 6.5 8.625 7.125 8.625 L 13.5 8.625 L 13.5 4.125 C 13.5 3.121094 14.714844 2.625 15.421875 3.328125 L 23.296875 11.203125 C 23.734375 11.644531 23.734375 12.355469 23.296875 12.796875 Z M 9 20.4375 L 9 18.5625 C 9 18.253906 8.746094 18 8.4375 18 L 4.5 18 C 3.671875 18 3 17.328125 3 16.5 L 3 7.5 C 3 6.671875 3.671875 6 4.5 6 L 8.4375 6 C 8.746094 6 9 5.746094 9 5.4375 L 9 3.5625 C 9 3.253906 8.746094 3 8.4375 3 L 4.5 3 C 2.015625 3 0 5.015625 0 7.5 L 0 16.5 C 0 18.984375 2.015625 21 4.5 21 L 8.4375 21 C 8.746094 21 9 20.746094 9 20.4375 Z M 9 20.4375 "/>
  //   </svg>,
  //   badge: 5,
  //   submenu: [
  //     { text: 'Nhập văn bản phát hành', path: '/outgoing-documents'},
  //     { text: 'Văn bản dự thảo', path: '/outgoing-documents?submenu=du_thao_van_ban', badge: 3 },
  //     { text: 'Văn bản chờ xử lý', path: '/outgoing-documents?submenu=van_ban_di_can_xu_ly', badge: 1 },
  //     { text: 'Văn bản đã xử lý', path: '/outgoing-documents?submenu=van_ban_di_da_xu_ly'},
  //     { text: 'Văn bản đã ban hành', path: '/outgoing-documents?submenu=van_ban_di_da_ban_hanh'},
  //   ]
  // },
  { 
    text: 'Quản lý văn bản đến', 
    path: '#', 
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M 19.5 21 L 15.5625 21 C 15.253906 21 15 20.746094 15 20.4375 L 15 18.5625 C 15 18.253906 15.253906 18 15.5625 18 L 19.5 18 C 20.328125 18 21 17.328125 21 16.5 L 21 7.5 C 21 6.671875 20.328125 6 19.5 6 L 15.5625 6 C 15.253906 6 15 5.746094 15 5.4375 L 15 3.5625 C 15 3.253906 15.253906 3 15.5625 3 L 19.5 3 C 21.984375 3 24 5.015625 24 7.5 L 24 16.5 C 24 18.984375 21.984375 21 19.5 21 Z M 17.296875 11.578125 L 9.421875 3.703125 C 8.71875 3 7.5 3.492188 7.5 4.5 L 7.5 9 L 1.125 9 C 0.5 9 0 9.5 0 10.125 L 0 14.625 C 0 15.25 0.5 15.75 1.125 15.75 L 7.5 15.75 L 7.5 20.25 C 7.5 21.257812 8.71875 21.75 9.421875 21.046875 L 17.296875 13.171875 C 17.734375 12.730469 17.734375 12.019531 17.296875 11.578125 Z M 17.296875 11.578125 "/>
    </svg>,
    badge: 8,
    submenu: [
      { text: 'Nhập văn bản', path: '/qlvb/incoming-documents?submenu=van_ban_duthao&openCreateDialog=true' },
      { text: 'Danh sách văn bản điện tử', path: '/qlvb/incoming-documents?submenu=van_ban_dientu', badge: 2 },
      { text: 'Văn bản dự thảo', path: '/qlvb/incoming-documents?submenu=van_ban_duthao' },
      { text: 'Văn bản chờ xử lý', path: '/qlvb/incoming-documents?submenu=van_ban_cho_xu_ly', badge: 1 },
      { text: 'Văn bản đã ban hành', path: '/qlvb/incoming-documents?submenu=van_ban_da_ban_hanh' },
      { text: 'Văn bản đã xử lý', path: '/qlvb/incoming-documents?submenu=van_ban_da_xu_ly' },
      { text: 'Văn bản xóa', path: '/qlvb/incoming-documents?submenu=van_ban_xoa' },
    ]
  },
  { 
    text: 'Xử lý hồ sơ công việc', 
    path: '#', 
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M 12 9.359375 C 11.117188 9.359375 10.398438 10.078125 10.398438 10.960938 L 10.398438 17.761719 C 10.398438 18.644531 11.117188 19.359375 12 19.359375 C 12.882812 19.359375 13.601562 18.644531 13.601562 17.761719 L 13.601562 10.960938 C 13.601562 10.078125 12.882812 9.359375 12 9.359375 Z M 12 9.359375 "/>
    <path d="M 12 0 C 5.390625 0 0 5.390625 0 12 C 0 18.609375 5.390625 24 12 24 C 18.609375 24 24 18.609375 24 12 C 24 5.390625 18.609375 0 12 0 Z M 12 3.199219 C 16.878906 3.199219 20.800781 7.121094 20.800781 12 C 20.800781 16.878906 16.878906 20.800781 12 20.800781 C 7.121094 20.800781 3.199219 16.878906 3.199219 12 C 3.199219 7.121094 7.121094 3.199219 12 3.199219 Z M 12 3.199219 "/>
    <path d="M 12 4.398438 C 10.914062 4.398438 10 5.3125 10 6.398438 C 10 7.484375 10.914062 8.398438 12 8.398438 C 13.085938 8.398438 14 7.484375 14 6.398438 C 14 5.3125 13.085938 4.398438 12 4.398438 Z M 12 4.398438 "/>
    </svg>,
    badge: 8,
    submenu: [
        { 
          text: 'Xử lý văn bản đến', 
          path: '#', 
          badge: 12,
          submenu: [
            { text: 'Văn bản đến chờ xử lý', path: '/xlvb/incoming-documents?submenu=van_ban_cho_xu_ly', badge: 8 },
            { text: 'Văn bản đến trả lại', path: '/xlvb/incoming-documents?submenu=van_ban_den_tra_lai', badge: 3 },
            { text: 'Văn bản đến đã xử lý', path: '/xlvb/incoming-documents?submenu=van_ban_da_xu_ly', badge: 3 },
            { text: 'Văn bản xem để biết', path: '/xlvb/incoming-documents?submenu=van_ban_xem_de_biet', badge: 1 },
          ]
        },
        { 
          text: 'Xử lý văn bản đi', 
          path: '#', 
          badge: 7,
          submenu: [
            { text: 'Văn bản đi dự thảo', path: '/xlvb/outgoing-documents/?submenu=van_ban_di_du_thao', badge: 2 },
            { text: 'Văn bản đi cần xử lý', path: '/xlvb/outgoing-documents?submenu=van_ban_di_can_xu_ly', badge: 3 },
            { text: 'Văn bản đi đã xử lý', path: '/xlvb/outgoing-documents?submenu=van_ban_di_da_xu_ly', badge: 1 },
            { text: 'Văn bản đi đã ban hành', path: '/xlvb/outgoing-documents?submenu=van_ban_di_da_ban_hanh', badge: 1 },
            { text: 'Văn bản đi xem để biết', path: '/xlvb/outgoing-documents?submenu=van_ban_di_xem_de_biet', badge: 1 },
          ]
        },
        { 
          text: 'Văn bản đảng/nội bộ', 
          path: '#', 
          submenu: [
            { text: 'Văn bản dự thảo', path: '/xlvb/internal-documents/?submenu=van_ban_duthao', badge: 2 },
            { text: 'Văn bản cần xử lý', path: '/xlvb/internal-documents/?submenu=van_ban_di_can_xu_ly', badge: 3 },
            { text: 'Văn bản đã xử lý', path: '/xlvb/internal-documents/?submenu=van_ban_di_da_xu_ly', badge: 1 },
            { text: 'Văn bản đã ban hành', path: '/xlvb/internal-documents/?submenu=van_ban_di_da_ban_hanh', badge: 1 },
            { text: 'Văn bản xem để biết', path: '/xlvb/internal-documents/?submenu=van_ban_di_xem_de_biet', badge: 1 },
          ]
        },     ]
  },
  { 
    text: 'Tra cứu văn bản', 
    path: '/tra-cuu-toanbo', 
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d="M9.29289 1.29289C9.48043 1.10536 9.73478 1 10 1H18C19.6569 1 21 2.34315 21 4V8C21 8.55228 20.5523 9 20 9C19.4477 9 19 8.55228 19 8V4C19 3.44772 18.5523 3 18 3H11V8C11 8.55228 10.5523 9 10 9H5V20C5 20.5523 5.44772 21 6 21H10C10.5523 21 11 21.4477 11 22C11 22.5523 10.5523 23 10 23H6C4.34315 23 3 21.6569 3 20V8C3 7.73478 3.10536 7.48043 3.29289 7.29289L9.29289 1.29289ZM6.41421 7H9V4.41421L6.41421 7ZM20.1716 18.7574C20.6951 17.967 21 17.0191 21 16C21 13.2386 18.7614 11 16 11C13.2386 11 11 13.2386 11 16C11 18.7614 13.2386 21 16 21C17.0191 21 17.967 20.6951 18.7574 20.1716L21.2929 22.7071C21.6834 23.0976 22.3166 23.0976 22.7071 22.7071C23.0976 22.3166 23.0976 21.6834 22.7071 21.2929L20.1716 18.7574ZM13 16C13 14.3431 14.3431 13 16 13C17.6569 13 19 14.3431 19 16C19 17.6569 17.6569 19 16 19C14.3431 19 13 17.6569 13 16Z" fill="currentColor"/>
    </svg>,
    submenu: [
      { text: 'Tra cứu toàn bộ VB', path: '/tra-cuu-toanbo' },
      { text: 'Tra cứu văn bản đến', path: '/tra-cuu-vbden' },
      { text: 'Tra cứu văn bản đi', path: '/tra-cuu-vbdi' },
      { text: 'Tra cứu VB đến liên quan', path: '/tra-cuu-vbden-lienquan' },
    ]
  },
  { 
    text: 'Quản lý công việc nội bộ', 
    path: '#', 
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M 11 4 L 7.199219 4 C 6.078125 4 5.519531 4 5.09375 4.21875 C 4.714844 4.410156 4.410156 4.714844 4.21875 5.09375 C 4 5.519531 4 6.078125 4 7.199219 L 4 16.800781 C 4 17.921875 4 18.480469 4.21875 18.90625 C 4.410156 19.285156 4.714844 19.589844 5.09375 19.78125 C 5.519531 20 6.078125 20 7.199219 20 L 16.800781 20 C 17.921875 20 18.480469 20 18.90625 19.78125 C 19.285156 19.589844 19.589844 19.285156 19.78125 18.90625 C 20 18.480469 20 17.921875 20 16.800781 L 20 12.5 M 15.5 5.5 L 18.328125 8.328125 M 10.761719 10.238281 L 17.410156 3.589844 C 18.191406 2.808594 19.457031 2.808594 20.238281 3.589844 C 21.019531 4.371094 21.019531 5.636719 20.238281 6.417969 L 13.378906 13.28125 C 12.617188 14.042969 12.234375 14.421875 11.800781 14.726562 C 11.417969 14.992188 11 15.214844 10.5625 15.386719 C 10.070312 15.582031 9.542969 15.6875 8.488281 15.902344 L 8 16 L 8.046875 15.667969 C 8.214844 14.492188 8.300781 13.90625 8.492188 13.355469 C 8.660156 12.871094 8.890625 12.40625 9.179688 11.980469 C 9.503906 11.496094 9.921875 11.078125 10.761719 10.238281 Z M 10.761719 10.238281 "/>
    </svg>, 
    badge: 3,
    submenu: [
      { text: 'Công việc', path: '#',
        submenu: [
          { text: 'Tất cả công việc nội bộ', path: '/tasks-new' },
          { text: 'Kế hoạch tháng', path: '/tasks-new/monthly-plan' },
          { text: 'Chỉ đạo điều hành', path: '/tasks-new/directive' },
          { text: 'Công việc từ văn bản', path: '/tasks-new/task-from-docs' },
        ]
      },
      { text: 'Công việc xin gia hạn', path: '#',
        submenu: [
          { text: 'Công việc xin gia hạn chờ duyệt', path: '/tasks-new/request-task-extension' },
          { text: 'Công việc xin gia hạn bị từ chối', path: '/tasks-new/request-task-extension-rejected' },
          { text: 'Công việc xin gia hạn đã duyệt', path: '/tasks-new/request-task-extension-approved' },
        ]
      },
      { text: 'Báo cáo', path: '#',
        submenu: [
          { text: 'Báo cáo tổng hợp cá nhân', path: '/tasks-new/personal-report' },
          { text: 'Báo cáo chi tiết cá nhân', path: '/tasks-new/multiple-personal-detail-reports' },
          { text: 'Báo cáo tổng hợp đơn vị', path: '/tasks-new/department-report' },
        ]
      },
    ]
  },
  { 
    text: 'Lịch công tác', 
    path: '/calendar', 
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M 4.5 7.5 L 4.5 18.65625 C 4.5 19.265625 5.015625 19.78125 5.648438 19.78125 L 18.585938 19.78125 C 19.21875 19.78125 19.734375 19.265625 19.734375 18.65625 L 19.734375 7.5 C 19.734375 6.890625 19.21875 6.375 18.585938 6.375 L 17.273438 6.375 L 17.273438 6.257812 C 17.273438 5.859375 17.15625 5.414062 16.875 5.085938 C 16.570312 4.78125 16.101562 4.546875 15.421875 4.546875 C 14.71875 4.546875 14.25 4.78125 13.96875 5.085938 C 13.664062 5.414062 13.546875 5.859375 13.546875 6.257812 L 13.546875 6.375 L 10.6875 6.375 L 10.6875 6.257812 C 10.6875 5.859375 10.570312 5.414062 10.289062 5.085938 C 9.984375 4.78125 9.515625 4.546875 8.8125 4.546875 C 8.132812 4.546875 7.664062 4.78125 7.382812 5.085938 C 7.078125 5.414062 6.960938 5.859375 6.960938 6.257812 L 6.960938 6.375 L 5.648438 6.375 C 5.015625 6.375 4.5 6.890625 4.5 7.5 Z M 6.046875 18.257812 L 6.046875 15.820312 L 8.789062 15.820312 L 8.789062 18.257812 Z M 6.046875 15.445312 L 6.046875 13.007812 L 8.789062 13.007812 L 8.789062 15.445312 Z M 6.046875 12.632812 L 6.046875 10.171875 L 8.789062 10.171875 L 8.789062 12.632812 Z M 8.085938 8.226562 L 8.085938 6.257812 C 8.085938 5.882812 8.320312 5.695312 8.8125 5.695312 C 9.328125 5.695312 9.5625 5.882812 9.5625 6.257812 L 9.5625 8.226562 C 9.5625 8.601562 9.328125 8.789062 8.8125 8.789062 C 8.320312 8.789062 8.085938 8.601562 8.085938 8.226562 Z M 9.164062 18.257812 L 9.164062 15.820312 L 11.929688 15.820312 L 11.929688 18.257812 Z M 9.164062 15.445312 L 9.164062 13.007812 L 11.929688 13.007812 L 11.929688 15.445312 Z M 9.164062 12.632812 L 9.164062 10.171875 L 11.929688 10.171875 L 11.929688 12.632812 Z M 12.304688 18.257812 L 12.304688 15.820312 L 15.070312 15.820312 L 15.070312 18.257812 Z M 12.304688 15.445312 L 12.304688 13.007812 L 15.070312 13.007812 L 15.070312 15.445312 Z M 12.304688 12.632812 L 12.304688 10.171875 L 15.070312 10.171875 L 15.070312 12.632812 Z M 15.445312 18.257812 L 15.445312 15.820312 L 18.1875 15.820312 L 18.1875 18.257812 Z M 15.445312 15.445312 L 15.445312 13.007812 L 18.1875 13.007812 L 18.1875 15.445312 Z M 15.445312 12.632812 L 15.445312 10.171875 L 18.1875 10.171875 L 18.1875 12.632812 Z M 14.671875 8.226562 L 14.671875 6.257812 C 14.671875 5.882812 14.90625 5.695312 15.421875 5.695312 C 15.890625 5.695312 16.148438 5.882812 16.148438 6.257812 L 16.148438 8.226562 C 16.148438 8.601562 15.914062 8.789062 15.421875 8.789062 C 14.90625 8.789062 14.671875 8.601562 14.671875 8.226562 Z M 14.671875 8.226562 "/>
    </svg>
  },
  { 
    text: 'Báo cáo thống kê', 
    path: '#', 
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>,
    submenu: [
      { text: 'Báo cáo văn bản đến', path: '/reports/incoming_docs' },
      { text: 'Báo cáo thống kê số liệu trong Cơ quan', path: '/reports/docs-within-organization' }
    ]
  },
  { 
    text: 'Quản lý tài liệu cá nhân', 
    path: '/hoso-canhan', 
    icon: <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
    <path d="M 23.820312 11.414062 C 23.5625 11.121094 23.113281 11.09375 22.816406 11.351562 L 20.722656 13.207031 L 14.808594 18.441406 C 14.4375 18.769531 13.960938 18.945312 13.472656 18.945312 C 13.332031 18.945312 13.191406 18.933594 13.046875 18.902344 L 9.664062 18.152344 L 5.582031 17.253906 L 2.71875 16.617188 L 2.3125 16.527344 C 2.179688 16.484375 2.066406 16.425781 1.964844 16.351562 C 1.800781 16.234375 1.671875 16.078125 1.585938 15.902344 C 1.496094 15.726562 1.445312 15.53125 1.445312 15.332031 C 1.445312 15.207031 1.46875 15.082031 1.503906 14.957031 C 1.546875 14.820312 1.613281 14.699219 1.691406 14.589844 C 1.804688 14.425781 1.960938 14.296875 2.136719 14.210938 C 2.3125 14.117188 2.507812 14.070312 2.707031 14.070312 C 2.742188 14.070312 2.773438 14.074219 2.808594 14.074219 L 5.757812 14.710938 L 12.601562 16.191406 C 13.449219 16.378906 14.335938 16.148438 14.984375 15.570312 L 19.191406 11.867188 L 23.488281 8.082031 C 23.875 7.738281 24.039062 7.210938 23.90625 6.710938 C 23.777344 6.210938 23.378906 5.828125 22.871094 5.71875 L 22.335938 5.605469 L 18.433594 4.761719 L 13.492188 3.695312 C 12.644531 3.507812 11.761719 3.738281 11.109375 4.3125 L 4.507812 10.113281 L 2.519531 11.859375 L 0.792969 13.375 C 0.722656 13.457031 0.660156 13.539062 0.605469 13.625 C 0.574219 13.664062 0.546875 13.699219 0.523438 13.738281 C 0.351562 13.96875 0.21875 14.234375 0.125 14.523438 C 0.0390625 14.789062 0 15.0625 0 15.332031 C 0 15.910156 0.1875 16.460938 0.515625 16.917969 C 0.679688 17.148438 0.878906 17.351562 1.113281 17.519531 C 1.34375 17.691406 1.609375 17.824219 1.898438 17.914062 L 1.929688 17.921875 L 4.941406 18.582031 L 7.792969 19.207031 L 12.746094 20.292969 C 12.984375 20.34375 13.230469 20.371094 13.472656 20.371094 C 14.304688 20.371094 15.113281 20.070312 15.75 19.511719 L 15.921875 19.363281 L 21.800781 14.15625 L 23.761719 12.421875 C 24.054688 12.160156 24.082031 11.710938 23.820312 11.414062 Z M 23.820312 11.414062 "/>
    </svg>
  },
  { 
    text: 'Hệ thống Files', 
    path: FILES_SYSTEM_URL || '#', 
    isExternal: true,
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/></svg>
  },
  { divider: true, path: '' },
];

// Menu items cho role TRUONG_PHONG_HCTC
const truongPhongHCTCMenuItems: MenuItem[] = [
  { text: 'Dashboard', path: '/dashboard', icon: <HomeIcon /> },
  { 
    text: 'Quản lý văn bản đến', 
    path: '#', 
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M 19.5 21 L 15.5625 21 C 15.253906 21 15 20.746094 15 20.4375 L 15 18.5625 C 15 18.253906 15.253906 18 15.5625 18 L 19.5 18 C 20.328125 18 21 17.328125 21 16.5 L 21 7.5 C 21 6.671875 20.328125 6 19.5 6 L 15.5625 6 C 15.253906 6 15 5.746094 15 5.4375 L 15 3.5625 C 15 3.253906 15.253906 3 15.5625 3 L 19.5 3 C 21.984375 3 24 5.015625 24 7.5 L 24 16.5 C 24 18.984375 21.984375 21 19.5 21 Z M 17.296875 11.578125 L 9.421875 3.703125 C 8.71875 3 7.5 3.492188 7.5 4.5 L 7.5 9 L 1.125 9 C 0.5 9 0 9.5 0 10.125 L 0 14.625 C 0 15.25 0.5 15.75 1.125 15.75 L 7.5 15.75 L 7.5 20.25 C 7.5 21.257812 8.71875 21.75 9.421875 21.046875 L 17.296875 13.171875 C 17.734375 12.730469 17.734375 12.019531 17.296875 11.578125 Z M 17.296875 11.578125 "/>
    </svg>,
    badge: 12,
    submenu: [
        { text: 'Văn bản chờ xử lý', path: '/qlvb/incoming-documents?submenu=van_ban_cho_xu_ly', badge: 1 },
        { text: 'Văn bản đã xử lý', path: '/qlvb/incoming-documents?submenu=van_ban_da_xu_ly', badge: 1 },
        { text: 'Văn bản xóa', path: '/qlvb/incoming-documents?submenu=van_ban_xoa' },

      ]
  },
  { 
    text: 'Xử lý hồ sơ công việc', 
    path: '#', 
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M 12 9.359375 C 11.117188 9.359375 10.398438 10.078125 10.398438 10.960938 L 10.398438 17.761719 C 10.398438 18.644531 11.117188 19.359375 12 19.359375 C 12.882812 19.359375 13.601562 18.644531 13.601562 17.761719 L 13.601562 10.960938 C 13.601562 10.078125 12.882812 9.359375 12 9.359375 Z M 12 9.359375 "/>
    <path d="M 12 0 C 5.390625 0 0 5.390625 0 12 C 0 18.609375 5.390625 24 12 24 C 18.609375 24 24 18.609375 24 12 C 24 5.390625 18.609375 0 12 0 Z M 12 3.199219 C 16.878906 3.199219 20.800781 7.121094 20.800781 12 C 20.800781 16.878906 16.878906 20.800781 12 20.800781 C 7.121094 20.800781 3.199219 16.878906 3.199219 12 C 3.199219 7.121094 7.121094 3.199219 12 3.199219 Z M 12 3.199219 "/>
    <path d="M 12 4.398438 C 10.914062 4.398438 10 5.3125 10 6.398438 C 10 7.484375 10.914062 8.398438 12 8.398438 C 13.085938 8.398438 14 7.484375 14 6.398438 C 14 5.3125 13.085938 4.398438 12 4.398438 Z M 12 4.398438 "/>
    </svg>,
    badge: 8,
    submenu: [
        { 
          text: 'Xử lý văn bản đến', 
          path: '#', 
          badge: 12,
          submenu: [
            { text: 'Văn bản đến chờ xử lý', path: '/xlvb/incoming-documents?submenu=van_ban_cho_xu_ly', badge: 8 },
            { text: 'Văn bản đến trả lại', path: '/xlvb/incoming-documents?submenu=van_ban_den_tra_lai', badge: 3 },
            { text: 'Văn bản đến đã xử lý', path: '/xlvb/incoming-documents?submenu=van_ban_da_xu_ly', badge: 3 },
            { text: 'Văn bản xem để biết', path: '/xlvb/incoming-documents?submenu=van_ban_xem_de_biet', badge: 1 },
          ]
        },
        { 
          text: 'Xử lý văn bản đi', 
          path: '#', 
          badge: 7,
          submenu: [
            { text: 'Văn bản đi dự thảo', path: '/xlvb/outgoing-documents/?submenu=van_ban_di_du_thao', badge: 2 },
            { text: 'Văn bản đi cần xử lý', path: '/xlvb/outgoing-documents?submenu=van_ban_di_can_xu_ly', badge: 3 },
            { text: 'Văn bản đi đã xử lý', path: '/xlvb/outgoing-documents?submenu=van_ban_di_da_xu_ly', badge: 1 },
            { text: 'Văn bản đi đã ban hành', path: '/xlvb/outgoing-documents?submenu=van_ban_di_da_ban_hanh', badge: 1 },
            { text: 'Văn bản đi xem để biết', path: '/xlvb/outgoing-documents?submenu=van_ban_di_xem_de_biet', badge: 1 },
          ]
        },
        { 
          text: 'Văn bản đảng/nội bộ', 
          path: '#', 
          submenu: [
            { text: 'Văn bản dự thảo', path: '/xlvb/internal-documents/?submenu=van_ban_duthao', badge: 2 },
            { text: 'Văn bản cần xử lý', path: '/xlvb/internal-documents/?submenu=van_ban_di_can_xu_ly', badge: 3 },
            { text: 'Văn bản đã xử lý', path: '/xlvb/internal-documents/?submenu=van_ban_di_da_xu_ly', badge: 1 },
            { text: 'Văn bản đã ban hành', path: '/xlvb/internal-documents/?submenu=van_ban_di_da_ban_hanh', badge: 1 },
            { text: 'Văn bản xem để biết', path: '/xlvb/internal-documents/?submenu=van_ban_di_xem_de_biet', badge: 1 },
          ]
        },
      ]
  },
  { 
    text: 'Tra cứu văn bản', 
    path: '/tra-cuu-toanbo', 
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d="M9.29289 1.29289C9.48043 1.10536 9.73478 1 10 1H18C19.6569 1 21 2.34315 21 4V8C21 8.55228 20.5523 9 20 9C19.4477 9 19 8.55228 19 8V4C19 3.44772 18.5523 3 18 3H11V8C11 8.55228 10.5523 9 10 9H5V20C5 20.5523 5.44772 21 6 21H10C10.5523 21 11 21.4477 11 22C11 22.5523 10.5523 23 10 23H6C4.34315 23 3 21.6569 3 20V8C3 7.73478 3.10536 7.48043 3.29289 7.29289L9.29289 1.29289ZM6.41421 7H9V4.41421L6.41421 7ZM20.1716 18.7574C20.6951 17.967 21 17.0191 21 16C21 13.2386 18.7614 11 16 11C13.2386 11 11 13.2386 11 16C11 18.7614 13.2386 21 16 21C17.0191 21 17.967 20.6951 18.7574 20.1716L21.2929 22.7071C21.6834 23.0976 22.3166 23.0976 22.7071 22.7071C23.0976 22.3166 23.0976 21.6834 22.7071 21.2929L20.1716 18.7574ZM13 16C13 14.3431 14.3431 13 16 13C17.6569 13 19 14.3431 19 16C19 17.6569 17.6569 19 16 19C14.3431 19 13 17.6569 13 16Z" fill="currentColor"/>
    </svg>,
    submenu: [
      { text: 'Tra cứu toàn bộ VB', path: '/tra-cuu-toanbo' },
      { text: 'Tra cứu văn bản đến', path: '/tra-cuu-vbden' },
      { text: 'Tra cứu văn bản đi', path: '/tra-cuu-vbdi' },
      { text: 'Tra cứu VB đến liên quan', path: '/tra-cuu-vbden-lienquan' },
    ]
  },
  { 
    text: 'Quản lý công việc nội bộ', 
    path: '#', 
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M 11 4 L 7.199219 4 C 6.078125 4 5.519531 4 5.09375 4.21875 C 4.714844 4.410156 4.410156 4.714844 4.21875 5.09375 C 4 5.519531 4 6.078125 4 7.199219 L 4 16.800781 C 4 17.921875 4 18.480469 4.21875 18.90625 C 4.410156 19.285156 4.714844 19.589844 5.09375 19.78125 C 5.519531 20 6.078125 20 7.199219 20 L 16.800781 20 C 17.921875 20 18.480469 20 18.90625 19.78125 C 19.285156 19.589844 19.589844 19.285156 19.78125 18.90625 C 20 18.480469 20 17.921875 20 16.800781 L 20 12.5 M 15.5 5.5 L 18.328125 8.328125 M 10.761719 10.238281 L 17.410156 3.589844 C 18.191406 2.808594 19.457031 2.808594 20.238281 3.589844 C 21.019531 4.371094 21.019531 5.636719 20.238281 6.417969 L 13.378906 13.28125 C 12.617188 14.042969 12.234375 14.421875 11.800781 14.726562 C 11.417969 14.992188 11 15.214844 10.5625 15.386719 C 10.070312 15.582031 9.542969 15.6875 8.488281 15.902344 L 8 16 L 8.046875 15.667969 C 8.214844 14.492188 8.300781 13.90625 8.492188 13.355469 C 8.660156 12.871094 8.890625 12.40625 9.179688 11.980469 C 9.503906 11.496094 9.921875 11.078125 10.761719 10.238281 Z M 10.761719 10.238281 "/>
    </svg>, 
    badge: 5,
    submenu: [
      { text: 'Công việc', path: '#',
        submenu: [
          { text: 'Tất cả công việc nội bộ', path: '/tasks-new' },
          { text: 'Kế hoạch tháng', path: '/tasks-new/monthly-plan' },
          { text: 'Chỉ đạo điều hành', path: '/tasks-new/directive' },
          { text: 'Công việc từ văn bản', path: '/tasks-new/task-from-docs' },
        ]
      },
      { text: 'Công việc xin gia hạn', path: '#',
        submenu: [
          { text: 'Công việc xin gia hạn chờ duyệt', path: '/tasks-new/request-task-extension' },
          { text: 'Công việc xin gia hạn bị từ chối', path: '/tasks-new/request-task-extension-rejected' },
          { text: 'Công việc xin gia hạn đã duyệt', path: '/tasks-new/request-task-extension-approved' },
        ]
      },
      { text: 'Báo cáo', path: '#',
        submenu: [
          { text: 'Báo cáo tổng hợp cá nhân', path: '/tasks-new/personal-report' },
          { text: 'Báo cáo chi tiết cá nhân', path: '/tasks-new/multiple-personal-detail-reports' },
          { text: 'Báo cáo tổng hợp đơn vị', path: '/tasks-new/department-report' },
        ]
      },
    ]
  },
  { 
    text: 'Lịch công tác', 
    path: '/calendar', 
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M 4.5 7.5 L 4.5 18.65625 C 4.5 19.265625 5.015625 19.78125 5.648438 19.78125 L 18.585938 19.78125 C 19.21875 19.78125 19.734375 19.265625 19.734375 18.65625 L 19.734375 7.5 C 19.734375 6.890625 19.21875 6.375 18.585938 6.375 L 17.273438 6.375 L 17.273438 6.257812 C 17.273438 5.859375 17.15625 5.414062 16.875 5.085938 C 16.570312 4.78125 16.101562 4.546875 15.421875 4.546875 C 14.71875 4.546875 14.25 4.78125 13.96875 5.085938 C 13.664062 5.414062 13.546875 5.859375 13.546875 6.257812 L 13.546875 6.375 L 10.6875 6.375 L 10.6875 6.257812 C 10.6875 5.859375 10.570312 5.414062 10.289062 5.085938 C 9.984375 4.78125 9.515625 4.546875 8.8125 4.546875 C 8.132812 4.546875 7.664062 4.78125 7.382812 5.085938 C 7.078125 5.414062 6.960938 5.859375 6.960938 6.257812 L 6.960938 6.375 L 5.648438 6.375 C 5.015625 6.375 4.5 6.890625 4.5 7.5 Z M 6.046875 18.257812 L 6.046875 15.820312 L 8.789062 15.820312 L 8.789062 18.257812 Z M 6.046875 15.445312 L 6.046875 13.007812 L 8.789062 13.007812 L 8.789062 15.445312 Z M 6.046875 12.632812 L 6.046875 10.171875 L 8.789062 10.171875 L 8.789062 12.632812 Z M 8.085938 8.226562 L 8.085938 6.257812 C 8.085938 5.882812 8.320312 5.695312 8.8125 5.695312 C 9.328125 5.695312 9.5625 5.882812 9.5625 6.257812 L 9.5625 8.226562 C 9.5625 8.601562 9.328125 8.789062 8.8125 8.789062 C 8.320312 8.789062 8.085938 8.601562 8.085938 8.226562 Z M 9.164062 18.257812 L 9.164062 15.820312 L 11.929688 15.820312 L 11.929688 18.257812 Z M 9.164062 15.445312 L 9.164062 13.007812 L 11.929688 13.007812 L 11.929688 15.445312 Z M 9.164062 12.632812 L 9.164062 10.171875 L 11.929688 10.171875 L 11.929688 12.632812 Z M 12.304688 18.257812 L 12.304688 15.820312 L 15.070312 15.820312 L 15.070312 18.257812 Z M 12.304688 15.445312 L 12.304688 13.007812 L 15.070312 13.007812 L 15.070312 15.445312 Z M 12.304688 12.632812 L 12.304688 10.171875 L 15.070312 10.171875 L 15.070312 12.632812 Z M 15.445312 18.257812 L 15.445312 15.820312 L 18.1875 15.820312 L 18.1875 18.257812 Z M 15.445312 15.445312 L 15.445312 13.007812 L 18.1875 13.007812 L 18.1875 15.445312 Z M 15.445312 12.632812 L 15.445312 10.171875 L 18.1875 10.171875 L 18.1875 12.632812 Z M 14.671875 8.226562 L 14.671875 6.257812 C 14.671875 5.882812 14.90625 5.695312 15.421875 5.695312 C 15.890625 5.695312 16.148438 5.882812 16.148438 6.257812 L 16.148438 8.226562 C 16.148438 8.601562 15.914062 8.789062 15.421875 8.789062 C 14.90625 8.789062 14.671875 8.601562 14.671875 8.226562 Z M 14.671875 8.226562 "/>
    </svg>
  },
  { 
    text: 'Báo cáo thống kê', 
    path: '#', 
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>,
    submenu: [
      { text: 'Báo cáo văn bản đến', path: '/reports/incoming_docs' },
      { text: 'Báo cáo thống kê số liệu trong Cơ quan', path: '/reports/docs-within-organization' }
    ]
  },
  { 
    text: 'Quản lý tài liệu cá nhân', 
    path: '/hoso-canhan', 
    icon: <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
    <path d="M 23.820312 11.414062 C 23.5625 11.121094 23.113281 11.09375 22.816406 11.351562 L 20.722656 13.207031 L 14.808594 18.441406 C 14.4375 18.769531 13.960938 18.945312 13.472656 18.945312 C 13.332031 18.945312 13.191406 18.933594 13.046875 18.902344 L 9.664062 18.152344 L 5.582031 17.253906 L 2.71875 16.617188 L 2.3125 16.527344 C 2.179688 16.484375 2.066406 16.425781 1.964844 16.351562 C 1.800781 16.234375 1.671875 16.078125 1.585938 15.902344 C 1.496094 15.726562 1.445312 15.53125 1.445312 15.332031 C 1.445312 15.207031 1.46875 15.082031 1.503906 14.957031 C 1.546875 14.820312 1.613281 14.699219 1.691406 14.589844 C 1.804688 14.425781 1.960938 14.296875 2.136719 14.210938 C 2.3125 14.117188 2.507812 14.070312 2.707031 14.070312 C 2.742188 14.070312 2.773438 14.074219 2.808594 14.074219 L 5.757812 14.710938 L 12.601562 16.191406 C 13.449219 16.378906 14.335938 16.148438 14.984375 15.570312 L 19.191406 11.867188 L 23.488281 8.082031 C 23.875 7.738281 24.039062 7.210938 23.90625 6.710938 C 23.777344 6.210938 23.378906 5.828125 22.871094 5.71875 L 22.335938 5.605469 L 18.433594 4.761719 L 13.492188 3.695312 C 12.644531 3.507812 11.761719 3.738281 11.109375 4.3125 L 4.507812 10.113281 L 2.519531 11.859375 L 0.792969 13.375 C 0.722656 13.457031 0.660156 13.539062 0.605469 13.625 C 0.574219 13.664062 0.546875 13.699219 0.523438 13.738281 C 0.351562 13.96875 0.21875 14.234375 0.125 14.523438 C 0.0390625 14.789062 0 15.0625 0 15.332031 C 0 15.910156 0.1875 16.460938 0.515625 16.917969 C 0.679688 17.148438 0.878906 17.351562 1.113281 17.519531 C 1.34375 17.691406 1.609375 17.824219 1.898438 17.914062 L 1.929688 17.921875 L 4.941406 18.582031 L 7.792969 19.207031 L 12.746094 20.292969 C 12.984375 20.34375 13.230469 20.371094 13.472656 20.371094 C 14.304688 20.371094 15.113281 20.070312 15.75 19.511719 L 15.921875 19.363281 L 21.800781 14.15625 L 23.761719 12.421875 C 24.054688 12.160156 24.082031 11.710938 23.820312 11.414062 Z M 23.820312 11.414062 "/>
    </svg>
  },
  { 
    text: 'Hệ thống Files', 
    path: FILES_SYSTEM_URL || '#',
    isExternal: true,
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/></svg>
  },
  { divider: true, path: '' },
];

const NewLayout: React.FC = () => {
  // QUAN TRỌNG: Khai báo authentication state TRƯỚC TIÊN để tránh lỗi "can't access before initialization"
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [badgeCounts, setBadgeCounts] = useState<BadgeCounts>({
    qlvb_den_cho_xu_ly: 0,
    qlvb_den_du_thao: 0,
    qlvb_den_dien_tu: 0,
    xulyvb_den_cho_xu_ly: 0,
    xulyvb_den_tra_lai: 0,
    xulyvb_den_xem_de_biet_chua_xem: 0,
    xulyvb_di_du_thao: 0,
    xulyvb_di_can_xu_ly: 0,
    xulyvb_noibo_can_xu_ly: 0,
    xulyvb_noibo_du_thao: 0,
    xulyvb_noibo_xem_de_biet_chua_xem: 0,
    xulyvb_di_xem_de_biet_chua_xem: 0,
    congviec_kehoach_thang_chua_thuc_hien: 0,
    congviec_kehoach_thang_dang_thuc_hien: 0,
    congviec_kehoach_thang_da_hoanthanh: 0,
    congviec_cddt_chua_thuc_hien: 0,
    congviec_cddt_dang_thuc_hien: 0,
    congviec_cddt_da_hoanthanh: 0,
  });
  const [badgeCountsLoaded, setBadgeCountsLoaded] = useState(false);
  const [taskBadgeCounts, setTaskBadgeCounts] = useState<{[key: string]: number}>({});
  const [openSubmenus, setOpenSubmenus] = useState<string[]>([]);
  const [selectedMenuItemPath, setSelectedMenuItemPath] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createDocumentDialogOpen, setCreateDocumentDialogOpen] = useState(false);
  const [createOutgoingDocumentDialogOpen, setCreateOutgoingDocumentDialogOpen] = useState(false);
  const [teleModalOpen, setTeleModalOpen] = useState(false);
  const [teleOtp, setTeleOtp] = useState('');
  const [teleLoading, setTeleLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState<AlertColor>('info');

  // Reset selected menu path when route changes
  useEffect(() => {
    setSelectedMenuItemPath(null);
  }, [location.pathname, location.search]);

  // Helper functions for menu selection
  const isSubmenuItemSelected = (submenuItem: MenuItem): boolean => {
    if (submenuItem.path && submenuItem.path !== '#') {
      // Các item tra cứu theo phòng: path có scope=department
      if (submenuItem.path.includes('scope=department')) {
        const basePath = submenuItem.path.split('?')[0];
        return location.pathname === basePath && location.search.includes('scope=department');
      }

      // Các item có tham số submenu (ví dụ xl vb)
      if (submenuItem.path.includes('submenu=')) {
        const submenuKey = submenuItem.path.split('submenu=')[1];
        const basePath = submenuItem.path.split('?')[0];
        
        // Kiểm tra cả path và submenu param
        return location.pathname === basePath && location.search.includes(`submenu=${submenuKey}`);
      }

      // Item không có query: so sánh trực tiếp path, bỏ qua URL có submenu
      if (location.search.includes('submenu=')) {
        return false; // Nếu URL có submenu param thì không match với item không có submenu
      }
      return location.pathname === submenuItem.path;
    }
    return false;
  };

  const isMainMenuItemSelected = (item: MenuItem, itemIndex: number): boolean => {
    if (location.pathname === item.path) {
      return true;
    }
    // Khi menu expanded, không highlight main menu nếu chỉ có submenu được selected
    if (expanded || isMobile) {
      return false;
    }
    if (item.submenu) {
      const hasSelectedSubmenu = item.submenu.some(subItem => {
        if (isSubmenuItemSelected(subItem)) {
          return true;
        }
        if (subItem.submenu) {
          return subItem.submenu.some(nestedItem => isSubmenuItemSelected(nestedItem));
        }
        return false;
      });
      
      // Nếu có submenu được selected, chỉ return true cho menu item đầu tiên
      if (hasSelectedSubmenu) {
        let menuKey = `menu-${itemIndex}-submenu`;
        if (!selectedMenuItemPath) {
          setSelectedMenuItemPath(menuKey);
          return true;
        }
        return selectedMenuItemPath === menuKey;
      }
    }
    return false;
  };

  const isMenuItemSelected = (item: MenuItem, itemIndex: number): boolean => {
    // Khi menu expanded, chỉ selected main menu nếu chính nó match path
    if (expanded || isMobile) {
      const isMainMenuSelected = location.pathname === item.path || 
             (item.path === '/dashboard' && location.pathname === '/');
      
      if (isMainMenuSelected) {
        let menuKey = `menu-${itemIndex}`;
        // Chỉ selected menu item đầu tiên được render
        if (!selectedMenuItemPath) {
          setSelectedMenuItemPath(menuKey);
          return true;
        }
        return selectedMenuItemPath === menuKey;
      }
      return false;
    }
    
    // Khi menu collapsed, sử dụng logic cũ
    const isSelected = location.pathname === item.path || 
           (item.path === '/dashboard' && location.pathname === '/') ||
           isMainMenuItemSelected(item, itemIndex);
    
    // Nếu có nhiều menu trùng path hoặc submenu path, chỉ selected menu đầu tiên
    if (isSelected) {
      // Tạo unique key dựa trên index và submenu path
      let menuKey = `menu-${itemIndex}`;
      if (item.submenu) {
        const matchingSubmenu = item.submenu.find(subItem => isSubmenuItemSelected(subItem));
        if (matchingSubmenu) {
          // Khi có submenu được selected, sử dụng index để đảm bảo unique
          menuKey = `menu-${itemIndex}-submenu`;
        }
      }
      
      if (!selectedMenuItemPath) {
        setSelectedMenuItemPath(menuKey);
        return true;
      }
      return selectedMenuItemPath === menuKey;
    }
    
    return false;
  };

  // Auto expand submenu when current route matches submenu item (only when menu is expanded)
  useEffect(() => {
    if (userRoles.length > 0 && (expanded || isMobile)) {
      const menuItems = getMenuItemsByRole();
      const newOpenSubmenus: string[] = [];
      
      menuItems.forEach(item => {
        if (item.submenu) {
          // Check if any submenu item matches current route
          const hasMatchingSubmenu = item.submenu.some(subItem => {
            if (isSubmenuItemSelected(subItem)) {
              return true;
            }
            // Check nested submenu
            if (subItem.submenu) {
              return subItem.submenu.some(nestedItem => isSubmenuItemSelected(nestedItem));
            }
            return false;
          });
          
          if (hasMatchingSubmenu) {
            newOpenSubmenus.push(item.text || '');
            
            // Also expand nested submenu if needed
            item.submenu.forEach(subItem => {
              if (subItem.submenu) {
                const hasMatchingNested = subItem.submenu.some(nestedItem => isSubmenuItemSelected(nestedItem));
                if (hasMatchingNested) {
                  newOpenSubmenus.push(subItem.text || '');
                }
              }
            });
          }
        }
      });
      
      setOpenSubmenus(newOpenSubmenus);
    } else if (!expanded && !isMobile) {
      // When menu is collapsed, clear all open submenus
      setOpenSubmenus([]);
    }
  }, [location.pathname, userRoles, expanded, isMobile]);

  // Tự động load badge counts ngay khi component mount (để badge hiển thị ngay từ đầu)
  useEffect(() => {
    // QUAN TRỌNG: Chỉ gọi API sau khi đã authenticated và có user roles
    if (authLoading || !isAuthenticated || userRoles.length === 0) {
      return;
    }
    
    // Chỉ load một lần
    if (badgeCountsLoaded) {
      return;
    }
    
    // Load statistics API cho badge văn bản
    const loadStatistics = async () => {
      try {
        const response = await axiosInstance.get('/qlvb/incoming-documents/statistics');
        if (response.data.code === 0) {
          const statsData = response.data.data;
          setBadgeCounts(statsData);
          
          // Dispatch event để cập nhật Dashboard nếu đang ở trang dashboard
          if (location.pathname === '/dashboard' || location.pathname === '/') {
            window.dispatchEvent(new CustomEvent('dashboardStatsUpdate', {
              detail: statsData
            }));
          }
        }
      } catch (error) {

      }
    };
    
    // Load task badges API cho badge công việc
    const loadTaskBadges = async () => {
      try {
        const response = await axiosInstance.get('/task_new_badges');
        if (response.data.code === 0) {
          const badgesData = response.data.data;
          const badgeCounts: {[key: string]: number} = {};
          
          badgesData.forEach((badge: {badge_name: string, count: number}) => {
            badgeCounts[badge.badge_name] = badge.count;
          });
          
          setTaskBadgeCounts(badgeCounts);
        } else {

          setTaskBadgeCounts({});
        }
      } catch (error) {

        setTaskBadgeCounts({});
      }
    };
    
    // Load cả hai API cùng lúc
    Promise.all([loadStatistics(), loadTaskBadges()]).then(() => {
      setBadgeCountsLoaded(true);
    });
  }, [isAuthenticated, authLoading, userRoles.length, badgeCountsLoaded, location.pathname]);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [hoveredItemPosition, setHoveredItemPosition] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const [isHoveringPopup, setIsHoveringPopup] = useState(false);

  // Load user và roles từ localStorage hoặc API
  useEffect(() => {
    const loadUserAndRoles = async () => {
      // Kiểm tra roles trong localStorage
      const savedRoles = JSON.parse(localStorage.getItem("roles") || "[]");
      
      if (savedRoles.length === 0) {
        // Nếu không có roles trong localStorage, gọi API
        try {
          const response = await axiosInstance.get("/auth/user");
          if (response.data.code === 0 && response.data.data.user) {
            const userRoles = response.data.data.user.roles || [];
            localStorage.setItem("roles", JSON.stringify(userRoles));
            setUserRoles(userRoles);
            setCurrentUser(response.data.data.user);
          }
        } catch (error) {

        }
      } else {
        // Có roles trong localStorage
        setUserRoles(savedRoles);
        
        // Load thông tin user từ localStorage
        const userInfo = localStorage.getItem('currentUser');
        if (userInfo) {
          const user = JSON.parse(userInfo);
          setCurrentUser(user);
        } else {
          // Nếu không có user info trong localStorage, gọi API
          try {
            const response = await axiosInstance.get("/auth/user");
            if (response.data.code === 0 && response.data.data.user) {
              setCurrentUser(response.data.data.user);
              localStorage.setItem('currentUser', JSON.stringify(response.data.data.user));
            }
          } catch (error) {

          }
        }
      }
    };
    
    loadUserAndRoles();
  }, []);


  // Hàm kiểm tra user có role cụ thể không
  const hasRole = (roleCode: string): boolean => {
    return userRoles.includes(roleCode);
  };

  // Hàm gọi API task_new_badges để lấy tất cả badges cùng lúc
  const fetchTaskBadgeCounts = async () => {
    try {
      const response = await axiosInstance.get('/task_new_badges');
      if (response.data.code === 0) {
        // API trả về array chứa tất cả badges
        const badgesData = response.data.data;
        const badgeCounts: {[key: string]: number} = {};
        
        // Chuyển đổi array thành object
        badgesData.forEach((badge: {badge_name: string, count: number}) => {
          badgeCounts[badge.badge_name] = badge.count;
        });
        
        setTaskBadgeCounts(badgeCounts);
      } else {

        // Set default values
        setTaskBadgeCounts({});
      }
    } catch (error) {

      // Set default values
      setTaskBadgeCounts({});
    }
  };

  // Hàm xử lý hover vào menu item để load badge counts
  const handleMenuHover = async (menuText: string) => {
    // QUAN TRỌNG: Chỉ gọi API sau khi đã authenticated
    if (!isAuthenticated || authLoading) {
      return;
    }
    
    // Chỉ gọi API cho các menu item cụ thể và chỉ gọi một lần
    const targetMenus = [
      'Quản lý văn bản đi',
      'Quản lý văn bản đến', 
      'Xử lý hồ sơ công việc',
      'Quản lý công việc nội bộ'
    ];

    if (targetMenus.includes(menuText) && !badgeCountsLoaded) {
      setBadgeCountsLoaded(true); // Đánh dấu đã bắt đầu load để tránh gọi nhiều lần
      
      if (menuText === 'Quản lý công việc nội bộ') {
        // Gọi API task_new_badges cho menu công việc nội bộ
        await fetchTaskBadgeCounts();
      } else {
        // Gọi API qlvb/incoming-documents/statistics cho các menu khác
        try {
          const response = await axiosInstance.get('/qlvb/incoming-documents/statistics');
          if (response.data.code === 0) {
            const statsData = response.data.data;
            setBadgeCounts(statsData);
            
            // Dispatch event để cập nhật Dashboard nếu đang ở trang dashboard
            if (location.pathname === '/dashboard' || location.pathname === '/') {
              window.dispatchEvent(new CustomEvent('dashboardStatsUpdate', {
                detail: statsData
              }));
            }
          }
        } catch (error) {

        }
      }
    }
  };

  // Hàm lấy badge count từ API statistics
  const getBadgeCount = (submenuKey: string, path?: string): number => {
    // Phân biệt dựa trên path để xác định đúng badge
    const isQlvb = path?.includes('/qlvb/incoming-documents');
    const isXlvb = path?.includes('/xlvb/');
    const isInternal = path?.includes('/xlvb/internal-documents');
    
    switch (submenuKey) {
      // Quản lý văn bản đến (chỉ cho qlvb)
      case 'van_ban_cho_xu_ly':
        if (isQlvb) {
          return badgeCounts.qlvb_den_cho_xu_ly || 0;
        }
        // Xử lý văn bản đến cho xlvb
        return badgeCounts.xulyvb_den_cho_xu_ly || 0;
      case 'van_ban_duthao':
        if (isQlvb) {
          return badgeCounts.qlvb_den_du_thao || 0;
        }
        // Văn bản nội bộ dự thảo
        if (isInternal) {
          return badgeCounts.xulyvb_noibo_du_thao || 0;
        }
        return 0;
      case 'van_ban_dientu':
        return badgeCounts.qlvb_den_dien_tu || 0;
      
      // Xử lý văn bản đến (xlvb)
      case 'van_ban_den_cho_xu_ly':
        return badgeCounts.xulyvb_den_cho_xu_ly || 0;
      case 'tra_lai':
      case 'van_ban_den_tra_lai':
        return badgeCounts.xulyvb_den_tra_lai || 0;
      
      // Xử lý văn bản đi
      case 'van_ban_di_du_thao':
        return badgeCounts.xulyvb_di_du_thao || 0;
      case 'van_ban_di_can_xu_ly':
        // Văn bản đi hoặc văn bản nội bộ
        if (isInternal) {
          return badgeCounts.xulyvb_noibo_can_xu_ly || 0;
        }
        return badgeCounts.xulyvb_di_can_xu_ly || 0;
      
      // Văn bản nội bộ
      case 'van_ban_noi_bo_dang':
        return badgeCounts.xulyvb_noibo_can_xu_ly || 0;
      
      // Văn bản xem để biết chưa xem
      case 'van_ban_di_xem_de_biet':
        if (isInternal) {
          return badgeCounts.xulyvb_noibo_xem_de_biet_chua_xem || 0;
        }
        return badgeCounts.xulyvb_di_xem_de_biet_chua_xem || 0;
      case 'van_ban_xem_de_biet':
        // Văn bản đến xem để biết chưa xem
        return badgeCounts.xulyvb_den_xem_de_biet_chua_xem || 0;
      
      // Các trường hợp khác không có trong API statistics
      case 'van_ban_da_xu_ly':
      case 'van_ban_di_da_ban_hanh':
      case 'van_ban_di_da_xu_ly':
      case 'van_ban_da_ban_hanh':
      case 'du_thao_van_ban':
        return 0;
      
      default:
        return 0;
    }
  };

  // Hàm tính tổng số văn bản cần xử lý (văn bản đến + văn bản đi + văn bản nội bộ)
  const getTotalCanXuLyCount = (): number => {
    const denCanXuLy = badgeCounts.xulyvb_den_cho_xu_ly || 0;
    const diCanXuLy = badgeCounts.xulyvb_di_can_xu_ly || 0;
    const noiBoCanXuLy = badgeCounts.xulyvb_noibo_can_xu_ly || 0;
    return denCanXuLy + diCanXuLy + noiBoCanXuLy;
  };

  // Hàm tính tổng số công việc (kế hoạch tháng + chỉ đạo điều hành)
  const getTotalCongViecCount = (): number => {
    // Tính tổng kế hoạch tháng
    let keHoachThang = 0;
    if (badgeCounts.congviec_kehoach_thang_chua_thuc_hien !== undefined ||
        badgeCounts.congviec_kehoach_thang_dang_thuc_hien !== undefined ||
        badgeCounts.congviec_kehoach_thang_da_hoanthanh !== undefined) {
      keHoachThang = (badgeCounts.congviec_kehoach_thang_chua_thuc_hien || 0) +
                     (badgeCounts.congviec_kehoach_thang_dang_thuc_hien || 0);
    } else {
      keHoachThang = taskBadgeCounts?.monthly_plan || 0;
    }
    
    // Tính tổng chỉ đạo điều hành
    let chiDaoDieuHanh = 0;
    if (badgeCounts.congviec_cddt_chua_thuc_hien !== undefined ||
        badgeCounts.congviec_cddt_dang_thuc_hien !== undefined ||
        badgeCounts.congviec_cddt_da_hoanthanh !== undefined) {
      chiDaoDieuHanh = (badgeCounts.congviec_cddt_chua_thuc_hien || 0) +
                       (badgeCounts.congviec_cddt_dang_thuc_hien || 0);
    } else {
      chiDaoDieuHanh = taskBadgeCounts?.directive || 0;
    }
    
    return keHoachThang + chiDaoDieuHanh;
  };

  // Hàm lấy badge count cho task menu items
  const getTaskBadgeCount = (menuText: string): number => {
    switch (menuText) {
      case 'Tất cả công việc nội bộ':
        return taskBadgeCounts.task_new_process || 0;
      case 'Kế hoạch tháng':
        // Nếu có từ statistics API thì ưu tiên dùng
        if (badgeCounts.congviec_kehoach_thang_chua_thuc_hien !== undefined ||
            badgeCounts.congviec_kehoach_thang_dang_thuc_hien !== undefined ||
            badgeCounts.congviec_kehoach_thang_da_hoanthanh !== undefined) {
          const total = (badgeCounts.congviec_kehoach_thang_chua_thuc_hien || 0) +
                       (badgeCounts.congviec_kehoach_thang_dang_thuc_hien || 0);
          return total > 0 ? total : (taskBadgeCounts?.monthly_plan || 0);
        }
        return taskBadgeCounts?.monthly_plan || 0;
      case 'Công việc từ văn bản':
        return taskBadgeCounts.task_from_docs || 0;
      case 'Chỉ đạo điều hành':
        // Nếu có từ statistics API thì ưu tiên dùng
        if (badgeCounts.congviec_cddt_chua_thuc_hien !== undefined ||
            badgeCounts.congviec_cddt_dang_thuc_hien !== undefined ||
            badgeCounts.congviec_cddt_da_hoanthanh !== undefined) {
          const total = (badgeCounts.congviec_cddt_chua_thuc_hien || 0) +
                       (badgeCounts.congviec_cddt_dang_thuc_hien || 0);
          return total > 0 ? total : (taskBadgeCounts?.directive || 0);
        }
        return taskBadgeCounts?.directive || 0;
      case 'Công việc xin gia hạn chờ duyệt':
        return taskBadgeCounts.waiting_approve_request_task_extension || 0;
      case 'Công việc xin gia hạn bị từ chối':
        return taskBadgeCounts.rejected_request_task_extension || 0;
      case 'Công việc xin gia hạn đã duyệt':
        return taskBadgeCounts.approved_request_task_extension || 0;
      default:
        return 0;
    }
  };

  // Hàm process menu items để thay thế badge từ API và kiểm tra quyền hiển thị menu
  const processMenuItems = (menuItems: MenuItem[]): MenuItem[] => {
    return menuItems.map(item => {
      // Xử lý menu item cấp 1 - xóa badge hardcode
      const processedItem = {
        ...item,
        badge: undefined
      };

      if (item.submenu) {
        // Xử lý đặc biệt cho menu "Quản lý công việc nội bộ"
        if (item.text === 'Quản lý công việc nội bộ') {
          // Lọc submenu dựa trên role của user
          let filteredSubmenu = [...item.submenu];
          
          // Kiểm tra nếu user không có roles ["LANH_DAO_BAN", "TRUONG_PHONG_HCTC", "TRUONG_PHONG"]
          // thì ẩn submenu "Công việc xin gia hạn chờ duyệt"
          const hasApprovalRole = hasRole('LANH_DAO_BAN') || hasRole('TRUONG_PHONG_HCTC') || hasRole('TRUONG_PHONG');
          if (!hasApprovalRole) {
            filteredSubmenu = filteredSubmenu.filter(subItem => 
              subItem.path !== '/tasks-new/request-task-extension'
            );
          }
          
          // Xử lý badge cho submenu đã lọc
          const processedSubmenu = filteredSubmenu.map(subItem => {
            if (subItem.submenu) {
              // Xử lý nested submenu với task badge counts
              const processedNestedSubmenu = subItem.submenu.map(nestedItem => {
                const badgeCount = getTaskBadgeCount(nestedItem.text || '');
                return {
                  ...nestedItem,
                  badge: badgeCount > 0 ? badgeCount : undefined
                };
              });
              return {
                ...subItem,
                submenu: processedNestedSubmenu
              };
            }
            return {
              ...subItem,
              badge: undefined
            };
          });
          
          return {
            ...processedItem,
            submenu: processedSubmenu
          };
        }
        
        // Xử lý các menu khác như bình thường
        const processedSubmenu = item.submenu.map(subItem => {
          // Xử lý menu item cấp 2 - xóa badge hardcode
          const processedSubItem = {
            ...subItem,
            badge: undefined
          };

          if (subItem.submenu) {
            const processedNestedSubmenu = subItem.submenu.map(nestedItem => {
              // Xử lý badge cho tất cả items có submenu key
              const submenuKey = nestedItem.path.split('submenu=')[1]?.split('&')[0]?.split('#')[0];
              if (submenuKey) {
                const badgeCount = getBadgeCount(submenuKey, nestedItem.path);
                return {
                  ...nestedItem,
                  badge: badgeCount > 0 ? badgeCount : undefined
                };
              }
              // Những item không có submenu key thì xóa badge
              return {
                ...nestedItem,
                badge: undefined
              };
            });
            return {
              ...processedSubItem,
              submenu: processedNestedSubmenu
            };
          }
          
          // Xử lý badge cho submenu cấp 2 không có nested submenu (ví dụ: "Quản lý văn bản đến" → "Văn bản chờ xử lý")
          // "Nhập văn bản" không có badge
          if (processedSubItem.text === 'Nhập văn bản') {
            return {
              ...processedSubItem,
              badge: undefined
            };
          }
          
          const submenuKey = processedSubItem.path?.split('submenu=')[1]?.split('&')[0]?.split('#')[0];
          if (submenuKey) {
            const badgeCount = getBadgeCount(submenuKey, processedSubItem.path);
            return {
              ...processedSubItem,
              badge: badgeCount > 0 ? badgeCount : undefined
            };
          }
          
          return processedSubItem;
        });
        return {
          ...processedItem,
          submenu: processedSubmenu
        };
      }
      return processedItem;
    });
  };

  // Hàm lấy menu items dựa trên role của user
  const getMenuItemsByRole = (): MenuItem[] => {   
    let menuItems: MenuItem[] = [];
    
    if (hasRole('TRUONG_PHONG_HCTC')) {
      menuItems = [...truongPhongHCTCMenuItems]; // Tạo bản copy để tránh mutate
    } else if (hasRole('VAN_THU_DI') || hasRole('VAN_THU_DEN')) {
      menuItems = [...vanThuDiMenuItems]; // Tạo bản copy để tránh mutate
    } else {
      // Mặc định hiển thị menu nhân viên
      menuItems = [...nhanVienMenuItems]; // Tạo bản copy để tránh mutate
    }

    // Thêm menu "Quản lý văn bản đi" cho role VAN_THU_DI hoặc TRA_CUU_TOANBO_VB_DI
    if (hasRole('VAN_THU_DI') || hasRole('TRA_CUU_TOANBO_VB_DI')) {
      // Kiểm tra xem menu "Quản lý văn bản đi" đã tồn tại chưa
      const qlvbDiExists = menuItems.some(item => item.text === 'Quản lý văn bản đi');
      
      if (!qlvbDiExists) {
        // Tìm vị trí menu "Quản lý văn bản đến" để thêm menu "Quản lý văn bản đi" ngay sau đó
        const qlvbDenIndex = menuItems.findIndex(item => item.text === 'Quản lý văn bản đến');
        const qlvbDiMenu: MenuItem = {
          text: 'Quản lý văn bản đi',
          path: '#',
          icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M 23.296875 12.796875 L 15.421875 20.671875 C 14.71875 21.375 13.5 20.882812 13.5 19.875 L 13.5 15.375 L 7.125 15.375 C 6.5 15.375 6 14.875 6 14.25 L 6 9.75 C 6 9.125 6.5 8.625 7.125 8.625 L 13.5 8.625 L 13.5 4.125 C 13.5 3.121094 14.714844 2.625 15.421875 3.328125 L 23.296875 11.203125 C 23.734375 11.644531 23.734375 12.355469 23.296875 12.796875 Z M 9 20.4375 L 9 18.5625 C 9 18.253906 8.746094 18 8.4375 18 L 4.5 18 C 3.671875 18 3 17.328125 3 16.5 L 3 7.5 C 3 6.671875 3.671875 6 4.5 6 L 8.4375 6 C 8.746094 6 9 5.746094 9 5.4375 L 9 3.5625 C 9 3.253906 8.746094 3 8.4375 3 L 4.5 3 C 2.015625 3 0 5.015625 0 7.5 L 0 16.5 C 0 18.984375 2.015625 21 4.5 21 L 8.4375 21 C 8.746094 21 9 20.746094 9 20.4375 Z M 9 20.4375 "/>
          </svg>,
          submenu: [
            { text: 'Danh sách văn bản đi', path: '/qlvb/outgoing-documents' },
          ]
        };
        
        if (qlvbDenIndex !== -1) {
          // Thêm menu "Quản lý văn bản đi" sau menu "Quản lý văn bản đến"
          menuItems.splice(qlvbDenIndex + 1, 0, qlvbDiMenu);
        } else {
          // Nếu không tìm thấy menu "Quản lý văn bản đến", thêm sau Dashboard (nếu có)
          const dashboardIndex = menuItems.findIndex(item => item.text === 'Dashboard');
          if (dashboardIndex !== -1) {
            menuItems.splice(dashboardIndex + 1, 0, qlvbDiMenu);
          } else {
            // Nếu không có Dashboard, thêm vào đầu danh sách
            menuItems.unshift(qlvbDiMenu);
          }
        }
      }
    }

    // Thêm menu tra cứu văn bản phòng cho role TRA_CUU_VB_PHONG
    if (hasRole('TRA_CUU_VB_PHONG')) {
      const existsTraCuuPhong = menuItems.some(item => item.text === 'Tra cứu văn bản phòng');
      if (!existsTraCuuPhong) {
        menuItems.push(traCuuVbPhongMenuItem);
      }
    }

    // Thêm menu Setting cho role ADMIN
    if (hasRole('ADMIN')) {
      menuItems = [...menuItems, {
        text: 'Cài đặt',
        path: '/settings',
        icon: <SettingsIcon />
      }];
    }

    // Thêm menu Setting cho Văn thư
    if (hasRole('VAN_THU_DI') || hasRole('VAN_THU_DEN')) {
      menuItems = [...menuItems, {
        text: 'Cài đặt văn thư',
        path: '/vanthu_settings',
        icon: <SettingsIcon />
      }];
    }

    // Đảm bảo Dashboard luôn ở đầu tiên
    const dashboardItem = menuItems.find(item => item.text === 'Dashboard');
    if (dashboardItem) {
      // Xóa Dashboard khỏi vị trí hiện tại
      const filteredItems = menuItems.filter(item => item.text !== 'Dashboard');
      // Đặt Dashboard ở đầu danh sách
      return processMenuItems([dashboardItem, ...filteredItems]);
    }

    return processMenuItems(menuItems);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleDrawer = () => {
    setExpanded(!expanded);
    // Khi thu gọn sidebar, ẩn tất cả submenu
    if (expanded) {
      setOpenSubmenus([]);
    }
  };

  const handleSubmenuToggle = (itemText: string) => {
    setOpenSubmenus(prev => {
      if (prev.includes(itemText)) {
        // Nếu đã mở thì đóng (xóa khỏi array)
        return prev.filter(text => text !== itemText);
      } else {
        const menuItems = getMenuItemsByRole();
        
        // Hàm tìm item và parent của nó trong menu tree
        const findItemAndParent = (items: MenuItem[], targetText: string, parentText: string = ''): { found: boolean; parentText: string } => {
          for (const item of items) {
            if (item.text === targetText) {
              return { found: true, parentText };
            }
            if (item.submenu) {
              const result = findItemAndParent(item.submenu, targetText, item.text || '');
              if (result.found) {
                return result;
              }
            }
          }
          return { found: false, parentText: '' };
        };
        
        // Hàm lấy tất cả items cùng level với parent
        const getSiblingsAtLevel = (items: MenuItem[], targetText: string, parentText: string): string[] => {
          if (!parentText) {
            // Level 1 - lấy tất cả items level 1
            return items.map(item => item.text).filter((text): text is string => Boolean(text));
          }
          
          // Tìm parent và lấy siblings
          const findSiblings = (items: MenuItem[], currentParent: string): string[] => {
            for (const item of items) {
              if (item.text === currentParent && item.submenu) {
                return item.submenu.map(subItem => subItem.text).filter((text): text is string => Boolean(text));
              }
              if (item.submenu) {
                const result = findSiblings(item.submenu, currentParent);
                if (result.length > 0) {
                  return result;
                }
              }
            }
            return [];
          };
          
          return findSiblings(items, parentText);
        };
        
        // Tìm item và parent
        const { found, parentText } = findItemAndParent(menuItems, itemText);
        
        if (found) {
          // Lấy tất cả siblings cùng level
          const siblings = getSiblingsAtLevel(menuItems, itemText, parentText);
          const otherSiblings = siblings.filter((text: string) => text !== itemText);
          
          // Đóng tất cả siblings cùng level, giữ nguyên các level khác
          const remainingSubmenus = prev.filter((text: string) => !otherSiblings.includes(text));
          return [...remainingSubmenus, itemText];
        } else {
          // Nếu không tìm thấy, chỉ thêm vào array
          return [...prev, itemText];
        }
      }
    });
  };

  const handleMenuItemHover = (itemText: string, event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const viewportPadding = 8;
    const minPopupHeight = 220;
    const initialTop = rect.top;
    const availableHeightFromInitialTop = window.innerHeight - initialTop - viewportPadding;
    const adjustedTop =
      availableHeightFromInitialTop < minPopupHeight
        ? Math.max(viewportPadding, window.innerHeight - minPopupHeight - viewportPadding)
        : initialTop;
    const maxHeight = Math.max(140, window.innerHeight - adjustedTop - viewportPadding);

    setHoveredItem(itemText);
    setHoveredItemPosition({
      top: adjustedTop,
      left: rect.right,
      maxHeight
    });
    
    // Gọi API để load badge counts khi hover vào các menu item cụ thể
    handleMenuHover(itemText);
  };

  const handleMenuItemLeave = () => {
    // Không xóa popup ngay lập tức, để cho phép hover vào popup
  };

  const handlePopupMouseEnter = () => {
    setIsHoveringPopup(true);
  };

  const handlePopupMouseLeave = () => {
    setIsHoveringPopup(false);
    setHoveredItem(null);
    setHoveredItemPosition(null);
    setBadgeCountsLoaded(false); // Reset để có thể gọi API lại khi hover lần sau
  };

  // Avatar menu handlers
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    logout();
    handleMenuClose();
  };

  const handleProfileSettings = () => {
    navigate('/profile-settings');
    handleMenuClose();
  };

  const handleOpenTelegramLink = () => {
    setTeleModalOpen(true);
    handleMenuClose();
  };

  const handleTeleConfirm = async () => {
    const otp = teleOtp.trim();
    if (!otp) return;
    try {
      setTeleLoading(true);
      const res = await axiosInstance.post('/ai/telegram/confirm-link', { otp });
      if (res.data?.code === 0) {
        setSnackMessage('Liên kết tài khoản Telegram thành công.');
        setSnackSeverity('success');
        setSnackOpen(true);
        setTeleModalOpen(false);
        setTeleOtp('');
      } else {
        setSnackMessage(res.data?.message || res.data?.msg || 'Không thể liên kết tài khoản Telegram.');
        setSnackSeverity(res.data?.code === 2 ? 'warning' : 'error');
        setSnackOpen(true);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        err?.message ||
        'Lỗi khi liên kết tài khoản Telegram.';
      setSnackMessage(msg);
      setSnackSeverity('error');
      setSnackOpen(true);
    } finally {
      setTeleLoading(false);
    }
  };

  // Handler cho navigation
  const handleNavigation = (path: string, isExternal: boolean = false) => {
    if (path && path !== '#') {
      // Xử lý external links - mở trong tab mới
      if (isExternal) {
        window.open(path, '_blank');
        return;
      }
      
      navigate(path);
      // Đóng mobile drawer nếu đang mở
      if (isMobile) {
        setMobileOpen(false);
      }
    }
  };

  const headerQuickLinks = [
    { label: 'Quản lý quy trình TOR', url: 'http://metro.chiaky.com/admin/login', icon: <AccountTreeIcon fontSize="small" />, isExternal: true },
    { label: 'Trình quản lý và xem mô hình BIM', url: BIM_SYSTEM_URL, icon: <BimIcon fontSize="small" />, isExternal: true },
    { label: 'Hệ thống Files', url: FILES_SYSTEM_URL, icon: <FolderIcon fontSize="small" />, isExternal: true },
    { label: 'Công việc theo dự án', url: TASK_PROJECT_DASHBOARD_URL, icon: <DashboardIcon fontSize="small" />, isExternal: false },
  ];
  const isStagingHost = window.location.hostname.endsWith('.mrbhn.com');

  const drawer = (isMobile: boolean) => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      overflow: 'auto'
    }}>
      <Toolbar sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: expanded || isMobile ? 'flex-start' : 'center',
        minHeight: '51px !important'
      }}>
        {expanded || isMobile ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <img 
                src="/logo.png"
                alt="Logo" 
                style={{ height: 32, width: 'auto' }} 
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                PMIS
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Ver 2.3.5
              </Typography>
            </Box>
            {!isMobile && (
              <IconButton
                onClick={toggleDrawer}
                sx={{ ml: 'auto' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </>
        ) : (
          <IconButton
            onClick={toggleDrawer}
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%'
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      
      {/* Menu Items */}
      <List sx={{ flexGrow: 1 }}>
        {userRoles.length > 0 && getMenuItemsByRole().map((item, index) => (
          item.divider ? (
            <Divider key={`divider-${index}`} />
          ) : (
            <React.Fragment key={`${item.text}-${index}`}>
              <ListItem disablePadding>
                <Box
                  sx={{
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  <ListItemButton
                    selected={isMenuItemSelected(item, index)}
                    onClick={() => {
                      if (item.submenu && item.path === '#') {
                        // Chỉ toggle submenu nếu path là '#'
                        handleSubmenuToggle(item.text || '');
                      } else {
                        // Navigate đến path (kể cả khi có submenu)
                        handleNavigation(item.path, item.isExternal);
                      }
                    }}
                    onMouseEnter={(event) => handleMenuItemHover(item.text || '', event)}
                    onMouseLeave={handleMenuItemLeave}
                    sx={{
                      minHeight: 38,
                      px: 1.25,
                      justifyContent: expanded || isMobile ? 'initial' : 'center',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#086428',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#086428',
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'white',
                        },
                      }
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: 0, 
                      mr: expanded || isMobile ? 1.5 : 'auto', 
                      justifyContent: 'center',
                      color: isMenuItemSelected(item, index) ? '#ffffff' : 'inherit'
                    }}>
                      {(!expanded && !isMobile && item.badge) ? (
                        <Badge 
                          badgeContent={item.badge} 
                          color="error"
                          sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.75rem',
                              height: '18px',
                              minWidth: '18px',
                              backgroundColor: '#f44336',
                              color: '#ffffff'
                            }
                          }}
                        >
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    {(expanded || isMobile) && (
                      <>
                        <ListItemText 
                          primary={item.text}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }
                          }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.badge && (
                            <Badge 
                              badgeContent={item.badge} 
                              color="error"
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '0.75rem',
                                  height: '18px',
                                  minWidth: '18px',
                                  backgroundColor: '#f44336',
                                  color: '#ffffff'
                                }
                              }}
                            >
                              <Box sx={{ width: 8, height: 8 }} />
                            </Badge>
                          )}
                          {/* Badge cho menu "Xử lý hồ sơ công việc" - hiển thị trước IconButton khi expanded */}
                          {(expanded || isMobile) && item.text === 'Xử lý hồ sơ công việc' && getTotalCanXuLyCount() > 0 && (
                            <Badge 
                              badgeContent={getTotalCanXuLyCount()} 
                              color="error"
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '0.75rem',
                                  height: '18px',
                                  minWidth: '18px',
                                  backgroundColor: '#f44336',
                                  color: '#ffffff'
                                }
                              }}
                            >
                              <Box sx={{ width: 8, height: 8 }} />
                            </Badge>
                          )}
                          {/* Badge cho menu "Quản lý công việc nội bộ" - hiển thị trước IconButton khi expanded */}
                          {(expanded || isMobile) && item.text === 'Quản lý công việc nội bộ' && getTotalCongViecCount() > 0 && (
                            <Badge 
                              badgeContent={getTotalCongViecCount()} 
                              color="error"
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '0.75rem',
                                  height: '18px',
                                  minWidth: '18px',
                                  backgroundColor: '#f44336',
                                  color: '#ffffff'
                                }
                              }}
                            >
                              <Box sx={{ width: 8, height: 8 }} />
                            </Badge>
                          )}
                          {item.submenu && (
                            <IconButton size="small">
                              {openSubmenus.includes(item.text || '') ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          )}
                        </Box>
                      </>
                    )}
                  </ListItemButton>
                  
                  {/* Badge bên ngoài ListItemButton cho menu "Xử lý hồ sơ công việc" - khi collapsed */}
                  {(!expanded && !isMobile) && item.text === 'Xử lý hồ sơ công việc' && getTotalCanXuLyCount() > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: '50%',
                        top: '50%',
                        transform: 'translate(50%, -50%)',
                        zIndex: 1,
                      }}
                    >
                      <Badge 
                        badgeContent={getTotalCanXuLyCount()} 
                        color="error"
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: '0.75rem',
                            height: '18px',
                            minWidth: '18px',
                            backgroundColor: '#f44336',
                            color: '#ffffff',
                            right: 0,
                          }
                        }}
                      >
                        <Box sx={{ width: 8, height: 8 }} />
                      </Badge>
                    </Box>
                  )}
                  
                  {/* Badge bên ngoài ListItemButton cho menu "Quản lý công việc nội bộ" - khi collapsed */}
                  {(!expanded && !isMobile) && item.text === 'Quản lý công việc nội bộ' && getTotalCongViecCount() > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: '50%',
                        top: '50%',
                        transform: 'translate(50%, -50%)',
                        zIndex: 1,
                      }}
                    >
                      <Badge 
                        badgeContent={getTotalCongViecCount()} 
                        color="error"
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: '0.75rem',
                            height: '18px',
                            minWidth: '18px',
                            backgroundColor: '#f44336',
                            color: '#ffffff',
                            right: 0,
                          }
                        }}
                      >
                        <Box sx={{ width: 8, height: 8 }} />
                      </Badge>
                    </Box>
                  )}
                  
                  {/* Popup khi hover trong trạng thái collapsed */}
                  {!expanded && !isMobile && hoveredItem === item.text && hoveredItemPosition && (
                    <Box
                      sx={{
                        position: 'fixed',
                        left: `${hoveredItemPosition.left}px`,
                        top: `${hoveredItemPosition.top}px`,
                        backgroundColor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        py: 0.5,
                        zIndex: 1400,
                        boxShadow: 3,
                        minWidth: '200px',
                        maxWidth: '300px',
                        maxHeight: `${hoveredItemPosition.maxHeight}px`,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        pointerEvents: 'auto'
                      }}
                      onMouseEnter={handlePopupMouseEnter}
                      onMouseLeave={handlePopupMouseLeave}
                    >
                      {item.submenu ? (
                        // Hiển thị tên menu chính và submenu
                        <>
                          {/* Tên menu chính */}
                          <Box
                            sx={{
                              px: 1.5,
                              py: 1,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              fontWeight: 'bold',
                              fontSize: '0.875rem',
                              overflow: 'hidden',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 'bold',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: 1
                                }}
                              >
                                {item.text}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Danh sách submenu */}
                          {item.submenu.map((subItem, subIndex) => (
                          <React.Fragment key={`${subItem.text}-${subIndex}`}>
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.75,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor: isSubmenuItemSelected(subItem) ? 'primary.main' : 'transparent',
                                color: isSubmenuItemSelected(subItem) ? 'primary.contrastText' : 'inherit',
                                '&:hover': {
                                  backgroundColor: isSubmenuItemSelected(subItem) ? 'primary.dark' : 'action.hover',
                                },
                                minWidth: 0,
                              }}
                              onClick={() => {
                                handleNavigation(subItem.path, subItem.isExternal);
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                                <Box sx={{ 
                                  width: 4, 
                                  height: 4, 
                                  borderRadius: '50%', 
                                  backgroundColor: isSubmenuItemSelected(subItem) ? 'primary.contrastText' : 'text.secondary',
                                  flexShrink: 0
                                }} />
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: isSubmenuItemSelected(subItem) ? 'primary.contrastText' : 'text.primary',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1
                                  }}
                                >
                                  {subItem.text}
                                </Typography>
                              </Box>
                              {subItem.badge && (
                                <Badge 
                                  badgeContent={subItem.badge} 
                                  color="error"
                                  sx={{
                                    flexShrink: 0,
                                    ml: 1,
                                    '& .MuiBadge-badge': {
                                      fontSize: '0.7rem',
                                      height: '16px',
                                      minWidth: '16px',
                                      backgroundColor: '#f44336',
                                      color: '#ffffff'
                                    }
                                  }}
                                >
                                  <Box sx={{ width: 8, height: 8 }} />
                                </Badge>
                              )}
                            </Box>
                            
                            {/* Nested submenu */}
                            {subItem.submenu && subItem.submenu.map((nestedItem, nestedIndex) => (
                              <Box
                                key={`${nestedItem.text}-nested-${nestedIndex}`}
                                sx={{
                                  px: 2.5,
                                  py: 0.5,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  backgroundColor: isSubmenuItemSelected(nestedItem) ? 'primary.main' : 'rgba(0, 0, 0, 0.02)',
                                  color: isSubmenuItemSelected(nestedItem) ? 'primary.contrastText' : 'inherit',
                                  '&:hover': {
                                    backgroundColor: isSubmenuItemSelected(nestedItem) ? 'primary.dark' : 'action.hover',
                                  },
                                  minWidth: 0,
                                }}
                                onClick={() => {
                                  handleNavigation(nestedItem.path, nestedItem.isExternal);
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                                  <Box sx={{ 
                                    width: 3, 
                                    height: 3, 
                                    borderRadius: '50%', 
                                    backgroundColor: isSubmenuItemSelected(nestedItem) ? 'primary.contrastText' : 'text.secondary',
                                    flexShrink: 0
                                  }} />
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: isSubmenuItemSelected(nestedItem) ? 'primary.contrastText' : 'text.primary',
                                      fontSize: '0.8rem',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flex: 1
                                    }}
                                  >
                                    {nestedItem.text}
                                  </Typography>
                                </Box>
                                {nestedItem.badge && (
                                  <Badge 
                                    badgeContent={nestedItem.badge} 
                                    color="error"
                                    sx={{
                                      flexShrink: 0,
                                      ml: 1,
                                      '& .MuiBadge-badge': {
                                        fontSize: '0.65rem',
                                        height: '14px',
                                        minWidth: '14px',
                                        backgroundColor: '#f44336',
                                        color: '#ffffff'
                                      }
                                    }}
                                  >
                                    <Box sx={{ width: 6, height: 6 }} />
                                  </Badge>
                                )}
                              </Box>
                            ))}
                            
                            {subIndex < (item.submenu?.length || 0) - 1 && (
                              <Divider sx={{ my: 0.25 }} />
                            )}
                          </React.Fragment>
                        ))}
                        </>
                      ) : (
                        // Hiển thị tên item nếu không có submenu
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'text.primary',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            {item.text}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </ListItem>
              
              {/* Submenu */}
              {item.submenu && (expanded || isMobile) && (
                <Collapse in={openSubmenus.includes(item.text || '')} timeout="auto" unmountOnExit>
                  <List 
                    component="div" 
                    disablePadding
                    sx={{
                      maxHeight: 'calc(100vh - 200px)',
                      overflow: 'auto'
                    }}
                  >
                    {item.submenu.map((subItem) => (
                      <React.Fragment key={`${subItem.text}-submenu`}>
                        <ListItem disablePadding>
                          <ListItemButton
                            selected={isSubmenuItemSelected(subItem)}
                            onClick={() => {
                              if (subItem.submenu) {
                                handleSubmenuToggle(subItem.text || '');
                              } else {
                                handleNavigation(subItem.path, subItem.isExternal);
                              }
                            }}
                            sx={{
                              pl: 4,
                              minHeight: 38,
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                              '&.Mui-selected': {
                                backgroundColor: '#086428',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: '#086428',
                                },
                              }
                            }}
                          >
                            <ListItemIcon sx={{ 
                              minWidth: 0, 
                              mr: 1.5, 
                              justifyContent: 'center',
                              color: isSubmenuItemSelected(subItem) ? '#ffffff' : 'inherit'
                            }}>
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText 
                              primary={subItem.text}
                              sx={{
                                '& .MuiListItemText-primary': {
                                  fontSize: '0.8rem'
                                }
                              }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {subItem.badge && (
                                <Badge 
                                  badgeContent={subItem.badge} 
                                  color="error"
                                  sx={{
                                    '& .MuiBadge-badge': {
                                      fontSize: '0.7rem',
                                      height: '16px',
                                      minWidth: '16px',
                                      backgroundColor: isSubmenuItemSelected(subItem) ? '#ffffff' : '#f44336',
                                      color: isSubmenuItemSelected(subItem) ? '#086428' : '#ffffff'
                                    }
                                  }}
                                >
                                  <Box sx={{ width: 6, height: 6 }} />
                                </Badge>
                              )}
                              {subItem.submenu && (
                                <IconButton size="small">
                                  {openSubmenus.includes(subItem.text || '') ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              )}
                            </Box>
                          </ListItemButton>
                        </ListItem>
                        
                        {/* Nested Submenu */}
                        {subItem.submenu && (
                          <Collapse in={openSubmenus.includes(subItem.text || '')} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                              {subItem.submenu.map((nestedItem) => (
                                <ListItem key={`${nestedItem.text}-nested-list`} disablePadding>
                                  <ListItemButton
                                    selected={isSubmenuItemSelected(nestedItem)}
                                    onClick={() => handleNavigation(nestedItem.path, nestedItem.isExternal)}
                                    sx={{
                                      pl: 6,
                                      minHeight: 38,
                                      '&:hover': {
                                        backgroundColor: 'action.hover',
                                      },
                                      '&.Mui-selected': {
                                        backgroundColor: '#086428',
                                        color: 'white',
                                        '&:hover': {
                                          backgroundColor: '#086428',
                                        },
                                      }
                                    }}
                                  >
                                    <ListItemIcon sx={{ 
                                      minWidth: 0, 
                                      mr: 1.5, 
                                      justifyContent: 'center',
                                      color: isSubmenuItemSelected(nestedItem) ? '#ffffff' : 'inherit'
                                    }}>
                                      {nestedItem.icon}
                                    </ListItemIcon>
                                    <ListItemText 
                                      primary={nestedItem.text}
                                      sx={{
                                        '& .MuiListItemText-primary': {
                                          fontSize: '0.75rem'
                                        }
                                      }}
                                    />
                                    {nestedItem.badge && (
                                      <Badge 
                                        badgeContent={nestedItem.badge} 
                                        color="error"
                                        sx={{
                                          '& .MuiBadge-badge': {
                                            fontSize: '0.65rem',
                                            height: '14px',
                                            minWidth: '14px',
                                            backgroundColor: isSubmenuItemSelected(nestedItem) ? '#ffffff' : '#f44336',
                                            color: isSubmenuItemSelected(nestedItem) ? '#086428' : '#ffffff'
                                          }
                                        }}
                                      >
                                        <Box sx={{ width: 4, height: 4 }} />
                                      </Badge>
                                    )}
                                  </ListItemButton>
                                </ListItem>
                              ))}
                            </List>
                          </Collapse>
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          )
        ))}
      </List>
      
      <Divider />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${expanded ? expandedWidth : collapsedWidth}px)` },
          ml: { sm: `${expanded ? expandedWidth : collapsedWidth}px` },
          transition: (theme) => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', height: '51px', p: 1 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          {isStagingHost && (
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'inherit',
                mr: 1,
              }}
            >
              TEST ENV
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
            {headerQuickLinks.map((link) => (
              <IconButton
                key={link.label}
                color="inherit"
                size="small"
                aria-label={link.label}
                title={link.label}
                onClick={() => handleNavigation(link.url, link.isExternal)}
              >
                {link.icon}
              </IconButton>
            ))}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {user && <NotificationBell />}
            <IconButton onClick={handleMenuClick} sx={{ p: 0.5 }}>
              <Avatar sx={{ width: 26, height: 26 }}>{user?.fullname?.charAt(0) || 'U'}</Avatar>
            </IconButton>
          </Box>
        </Box>
      </AppBar>
      
      <Box
        component="nav"
        className="main-sidebar"
        sx={{
          width: { sm: expanded ? expandedWidth : collapsedWidth },
          flexShrink: { sm: 0 },
          display: { xs: 'none', sm: 'block' }
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: expanded ? expandedWidth : collapsedWidth,
              transition: 'width 0.2s ease-in-out',
              boxSizing: 'border-box',
              backgroundColor: 'background.paper',
              overflowX: 'hidden',
            },
          }}
        >
          {drawer(false)}
        </Drawer>
      </Box>
      
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: expandedWidth,
          },
        }}
      >
        {drawer(true)}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { 
            xs: '100vw',
            sm: `calc(100vw - ${expanded ? expandedWidth : collapsedWidth}px)` 
          },
          ml: 0,
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          transition: (theme) => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Box 
          sx={{ 
            flexGrow: 1, 
            position: 'absolute',
            top: 51, // Toolbar height
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            p: 0, // Set padding to 0
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Avatar Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            width: 256,
            maxHeight: 'calc(100vh - 100px)',
            mt: 1.5,
            overflow: 'auto',
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            },
            '& .MuiMenuItem-root.logout-item': {
              fontSize: 16,
              typography: 'body1',
            },
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2 }}>
          <Typography variant="caption" display="block" noWrap sx={{ color: 'text.secondary' }}>
            {user?.email}
          </Typography>
          <Typography variant="subtitle2" noWrap>
            {user?.fullname}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleProfileSettings}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Cài đặt tài khoản" />
        </MenuItem>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleOpenTelegramLink}>
          <ListItemIcon>
            <TelegramIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="Liên kết Telegram" />
        </MenuItem>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 600 }}>
            Vai trò
          </Typography>
          <List sx={{ 
            maxHeight: 160, 
            overflow: 'auto',
            '& .MuiListItemButton-root': {
              backgroundColor: 'inherit',
              color: 'inherit',
            }
          }}>
            {user?.roles?.map((role) => (
              <ListItemButton
                key={role}
                sx={{ 
                  height: 38,
                  px: 1.25
                }}
              >
                <ListItemText primary={role} sx={{ 
                  '& .MuiListItemText-primary': {
                    fontSize: 13
                  }
                 }}/>
              </ListItemButton>
            ))}
          </List>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem 
          onClick={handleLogout} 
          className="logout-item"
          sx={{ m: 1, color: 'error.main' }}
        >
          Đăng xuất
        </MenuItem>
      </Menu>

      {/* Dialog Tạo mới văn bản đến */}
      <CreateIncomingDocumentDialog
        open={createDocumentDialogOpen}
        onClose={() => setCreateDocumentDialogOpen(false)}
        onSuccess={() => {
          setCreateDocumentDialogOpen(false);
          // Có thể thêm logic refresh data nếu cần
        }}
        onReopen={() => {
          setCreateDocumentDialogOpen(true);
        }}
      />

      {/* Dialog Tạo mới văn bản đi */}
      <CreateDocumentDialog
        open={createOutgoingDocumentDialogOpen}
        onClose={() => setCreateOutgoingDocumentDialogOpen(false)}
        onSuccess={() => {
          setCreateOutgoingDocumentDialogOpen(false);
          // Có thể thêm logic refresh data nếu cần
        }}
      />

      <Dialog
        open={teleModalOpen}
        onClose={() => {
          if (!teleLoading) {
            setTeleModalOpen(false);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: 16,
            fontWeight: 600,
            bgcolor: '#086428',
            color: 'white',
            py: 1.25,
            px: 2.5,
          }}
        >
          Liên kết tài khoản Telegram
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Trên Telegram, gõ lệnh <strong>/link</strong> với bot PMIS để nhận mã OTP, sau đó nhập vào ô bên dưới để liên
            kết với tài khoản hiện tại.
          </Typography>
          <TextField
            label="Mã OTP Telegram"
            value={teleOtp}
            onChange={(e) => setTeleOtp(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ maxLength: 20 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setTeleModalOpen(false)}
            size="small"
            disabled={teleLoading}
          >
            Đóng
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={teleLoading || !teleOtp.trim()}
            onClick={handleTeleConfirm}
          >
            {teleLoading ? 'Đang liên kết...' : 'Xác nhận'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewLayout;
